import { useState, useEffect, useRef } from 'react';
import Navigation from '@/components/Navigation';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Users, BookOpen, Shield, ExternalLink, MessageCircle, CreditCard, Home, Truck, FileText, MapPin, DollarSign, Heart, CheckCircle, AlertCircle, Info, Star, Award, Calendar, Clock, Users as UsersIcon, Map, Compass, Flag, Target, Key, Lock, Mail, Phone, Building, Package, ShoppingCart, Gift, Upload, Download, RefreshCw, Check, Database, Copy, Image, GripVertical, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { processImageForUpload, isValidImageType, formatFileSize, ACCEPTED_IMAGE_TYPES } from '@/utils/imageUtils';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper to get full image URL (handles relative and absolute URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

// Convert image URL to thumbnail URL for faster loading in galleries
const getThumbUrl = (url) => {
  if (!url) return null;
  // Handle external URLs - return as-is (no thumbnails for external)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Convert /api/media/xxx to /api/media/thumb/xxx
  if (url.startsWith('/api/media/') && !url.includes('/thumb/')) {
    const filename = url.replace('/api/media/', '');
    return `${process.env.REACT_APP_BACKEND_URL}/api/media/thumb/${filename}`;
  }
  // Already a thumb URL or other format
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

// Sortable Answer Option Component
const SortableAnswerOption = ({ id, option, idx, questionForm, setQuestionForm, optionsCount }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2 mb-3 p-3 border rounded-lg bg-gray-50">
      {/* Drag handle */}
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded touch-none">
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>
      
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium w-20">Option {idx + 1}</span>
          <input
            type="checkbox"
            checked={Array.isArray(questionForm.correct) 
              ? questionForm.correct.includes(idx) 
              : questionForm.correct === idx}
            onChange={() => {
              const currentCorrect = (Array.isArray(questionForm.correct) 
                ? questionForm.correct 
                : [questionForm.correct]).filter(v => v !== null && v !== undefined);
              
              let newCorrect;
              if (currentCorrect.includes(idx)) {
                // Remove this index (but keep at least one)
                newCorrect = currentCorrect.filter(i => i !== idx);
                if (newCorrect.length === 0) newCorrect = [idx]; // Can't have zero correct
              } else {
                // Add this index
                newCorrect = [...currentCorrect, idx].sort((a, b) => a - b);
              }
              setQuestionForm({ ...questionForm, correct: newCorrect });
            }}
            className="w-4 h-4 accent-green-600"
          />
          <label className="text-sm text-green-600 font-medium">Correct</label>
        </div>
        <Input
          value={option.text || ''}
          onChange={(e) => {
            const newOptions = [...questionForm.options];
            newOptions[idx] = { ...newOptions[idx], text: e.target.value };
            setQuestionForm({ ...questionForm, options: newOptions });
          }}
          placeholder="Answer text (optional if using photo)"
        />
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                if (file.size > 8 * 1024 * 1024) {
                  alert('Option image must be less than 8MB');
                  e.target.value = '';
                  return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                  const newOptions = [...questionForm.options];
                  newOptions[idx] = { ...newOptions[idx], photo_url: reader.result };
                  setQuestionForm({ ...questionForm, options: newOptions });
                };
                reader.readAsDataURL(file);
              }
            }}
            className="text-xs w-full"
          />
          {option.photo_url && (
            <button
              type="button"
              onClick={() => {
                const newOptions = [...questionForm.options];
                newOptions[idx] = { ...newOptions[idx], photo_url: '' };
                setQuestionForm({ ...questionForm, options: newOptions });
              }}
              className="text-red-500 text-xs hover:text-red-700"
            >
              Remove
            </button>
          )}
        </div>
        {option.photo_url && (
          <img src={option.photo_url} alt={`Option ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
        )}
      </div>
      {optionsCount > 2 && (
        <button
          type="button"
          onClick={() => {
            const newOptions = questionForm.options.filter((_, i) => i !== idx);
            // Handle array-based correct field, filtering out nulls
            const currentCorrect = (Array.isArray(questionForm.correct) 
              ? questionForm.correct 
              : [questionForm.correct]).filter(v => v !== null && v !== undefined);
            
            let newCorrect = currentCorrect
              .filter(ci => ci !== idx) // Remove deleted index
              .map(ci => ci > idx ? ci - 1 : ci); // Adjust indices after deletion
            
            // Ensure at least one correct answer
            if (newCorrect.length === 0) newCorrect = [0];
            
            setQuestionForm({ ...questionForm, options: newOptions, correct: newCorrect });
          }}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const AdminPanel = ({ user, logout }) => {
  // Tab state - controlled to prevent reset during async operations
  const [activeAdminTab, setActiveAdminTab] = useState('modules');
  
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingModule, setEditingModule] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [showOutlineDialog, setShowOutlineDialog] = useState(false);
  const [outlineContent, setOutlineContent] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Resource management states
  const [resources, setResources] = useState([]);
  const [editingResource, setEditingResource] = useState(null);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    link: '',
    category: 'general',
    order: 0
  });

  // Trainer management states
  const [trainers, setTrainers] = useState([]);
  const [editingTrainer, setEditingTrainer] = useState(null);

  // App settings states
  const [appSettings, setAppSettings] = useState({ logo_url: null, app_name: '' });
  const [logoFile, setLogoFile] = useState(null);
  const logoInputRef = useRef(null);
  const [showTrainerDialog, setShowTrainerDialog] = useState(false);
  const [trainerForm, setTrainerForm] = useState({
    name: '',
    photo_url: '',
    bio: '',
    city: '',
    state: '',
    specialties: [],
    availability: 'Available',
    response_time: 'Within 24 hours'
  });
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const trainerPhotoInputRef = useRef(null);

  // Pricing management states
  const [pricingPlans, setPricingPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [planForm, setPlanForm] = useState({
    plan_type: '',
    name: '',
    description: '',
    price: 0,
    duration_days: null
  });

  // Adoption guide states
  const [adoptionSections, setAdoptionSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [sectionForm, setSectionForm] = useState({
    title: '',
    content: '',
    order: 0,
    icon: '',
    icon_image_url: ''
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  // Dashboard modules states
  const [dashboardModules, setDashboardModules] = useState([]);
  const [editingDashModule, setEditingDashModule] = useState(null);
  const [showDashModuleDialog, setShowDashModuleDialog] = useState(false);
  const [dashModuleForm, setDashModuleForm] = useState({
    title: '',
    description: '',
    icon_name: '',
    icon_image_url: '',
    bg_color: '#6FCFD3'
  });
  const [uploadingDashIcon, setUploadingDashIcon] = useState(false);
  const dashIconInputRef = useRef(null);

  // Herd Settings states
  const [herdSettings, setHerdSettings] = useState(null);
  const [editingDailyCareCategory, setEditingDailyCareCategory] = useState(null);
  const [editingTrainingCategory, setEditingTrainingCategory] = useState(null);
  const [showDailyCareDialog, setShowDailyCareDialog] = useState(false);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [dailyCareForm, setDailyCareForm] = useState({ key: '', label: '', icon_url: '', items: [] });
  const [trainingForm, setTrainingForm] = useState({ key: '', label: '', items: [] });
  const herdCategoryIconInputRef = useRef(null);
  const [uploadingHerdIcon, setUploadingHerdIcon] = useState(false);

  // Community Members states
  const [communityMembers, setCommunityMembers] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);

  // Media Library states
  const [mediaLibrary, setMediaLibrary] = useState({ items: [], total: 0 });
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaPage, setMediaPage] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaUploadInputRef = useRef(null);
  
  // Photo Gallery pagination
  const [photoGalleryTotal, setPhotoGalleryTotal] = useState(0);
  const [photoGalleryHasMore, setPhotoGalleryHasMore] = useState(false);

  // Virtual Corral states
  const [virtualCorralSettings, setVirtualCorralSettings] = useState(null);
  const [vcSettingsForm, setVcSettingsForm] = useState({
    monthly_price: 9.99,
    max_mustangs_per_event: 4
  });

  // Ranch Game states
  const [ranchGameSettings, setRanchGameSettings] = useState(null);
  const [ranchGameForm, setRanchGameForm] = useState({
    game_title: 'My Ranch Game',
    game_description: 'Build and manage your virtual ranch with interactive gameplay',
    is_enabled: false,
    coming_soon_message: 'The Ranch Game is currently under development and will be available soon!',
    feature_image_url: '',
    release_date: ''
  });
  const [uploadingRanchImage, setUploadingRanchImage] = useState(false);
  const ranchImageInputRef = useRef(null);

  // Coupon Code states
  const [coupons, setCoupons] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    description: '',
    max_uses: null,
    expiration_date: ''
  });

  // Mercantile states
  const [mercantileItems, setMercantileItems] = useState([]);
  const [showMercantileDialog, setShowMercantileDialog] = useState(false);
  const [editingMercantileItem, setEditingMercantileItem] = useState(null);
  const [mercantileForm, setMercantileForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: '',
    subcategory: '',
    image_url: '',
    item_type: 'inventory',
    inventory_key: '',
    ranch_asset_key: '',
    stock: null
  });
  const [mercantileCategories, setMercantileCategories] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', display_name: '' });

  // Coin Activities states
  const [coinActivities, setCoinActivities] = useState([]);
  const [showCoinActivityDialog, setShowCoinActivityDialog] = useState(false);
  const [coinActivityForm, setCoinActivityForm] = useState({
    activity_name: '',
    activity_type: '',
    coins_reward: 0,
    description: '',
    max_per_day: null
  });

  // User creation states
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: true
  });

  // Subscription management states
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [grantSubscriptionEmail, setGrantSubscriptionEmail] = useState('');
  const [grantSubscriptionDays, setGrantSubscriptionDays] = useState(30);

  // Virtual Mustang management states
  const [virtualMustangs, setVirtualMustangs] = useState([]);
  const [showVirtualMustangDialog, setShowVirtualMustangDialog] = useState(false);
  const [editingVirtualMustang, setEditingVirtualMustang] = useState(null);
  const [virtualMustangForm, setVirtualMustangForm] = useState({
    name: '',
    age: 0,
    gender: '',
    color: '',
    hma: '',
    height: '',
    disposition: '',
    freezemark: '',
    photo_url: '',
    photo_urls: [],
    adoption_method: 'olc',
    image_position: 'center center' // Options: left/center/right + top/center/bottom
  });
  const [uploadingMustangPhoto, setUploadingMustangPhoto] = useState(false);
  const [quickUploadMustangId, setQuickUploadMustangId] = useState(null);
  const virtualMustangPhotoInputRef = useRef(null);
  const quickPhotoInputRef = useRef(null);

  // Ranch Blocks states
  const [ranchBlocks, setRanchBlocks] = useState([]);
  const [showRanchBlockDialog, setShowRanchBlockDialog] = useState(false);
  const [editingRanchBlock, setEditingRanchBlock] = useState(null);
  const [ranchBlockForm, setRanchBlockForm] = useState({
    title: '',
    html_content: '',
    css_content: '',
    js_content: '',
    assets: [],
    position: 'main',
    is_active: true,
    order: 0
  });

  // Available icons for adoption guide sections
  const availableIcons = [
    { name: 'Home', component: Home, label: 'Home/Facilities' },
    { name: 'Truck', component: Truck, label: 'Transportation' },
    { name: 'FileText', component: FileText, label: 'Document/Application' },
    { name: 'MapPin', component: MapPin, label: 'Location' },
    { name: 'DollarSign', component: DollarSign, label: 'Money/Cost' },
    { name: 'Heart', component: Heart, label: 'Heart/Love' },
    { name: 'CheckCircle', component: CheckCircle, label: 'Check/Complete' },
    { name: 'AlertCircle', component: AlertCircle, label: 'Alert/Important' },
    { name: 'Info', component: Info, label: 'Information' },
    { name: 'Star', component: Star, label: 'Star/Featured' },
    { name: 'Award', component: Award, label: 'Award/Achievement' },
    { name: 'Calendar', component: Calendar, label: 'Calendar/Schedule' },
    { name: 'Clock', component: Clock, label: 'Time/Duration' },
    { name: 'Users', component: UsersIcon, label: 'People/Community' },
    { name: 'Map', component: Map, label: 'Map/Navigation' },
    { name: 'Compass', component: Compass, label: 'Compass/Direction' },
    { name: 'Flag', component: Flag, label: 'Flag/Goal' },
    { name: 'Target', component: Target, label: 'Target/Goal' },
    { name: 'Key', component: Key, label: 'Key/Access' },
    { name: 'Lock', component: Lock, label: 'Security/Protected' },
    { name: 'Mail', component: Mail, label: 'Email/Contact' },
    { name: 'Phone', component: Phone, label: 'Phone/Contact' },
    { name: 'Building', component: Building, label: 'Building/Facility' },
    { name: 'Package', component: Package, label: 'Package/Delivery' },
    { name: 'ShoppingCart', component: ShoppingCart, label: 'Shopping/Purchase' },
    { name: 'Gift', component: Gift, label: 'Gift/Bonus' }
  ];

  // Module form state
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    order: 0
  });

  // Question form state
  const [questionForm, setQuestionForm] = useState({
    question: '',
    options: [
      { text: '', photo_url: '' },
      { text: '', photo_url: '' }
    ],  // Start with minimum 2 options
    correct: [0],  // Array of correct answer indices (supports multiple correct)
    explanation: '',
    order: 0,
    section: 'adoption___application',
    photo_url: ''
  });

  // Database Backup states
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [seedingAdoption, setSeedingAdoption] = useState(false);
  const [seedingModules, setSeedingModules] = useState(false);
  const [seedingTransport, setSeedingTransport] = useState(false);
  const [migratingMedia, setMigratingMedia] = useState(false);
  
  // Photo Gallery states
  const [unusedPhotos, setUnusedPhotos] = useState([]);
  const [mustangsNeedingPhotos, setMustangsNeedingPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [selectedPhotoForAssign, setSelectedPhotoForAssign] = useState(null);
  const [selectedPhotosForBulkAssign, setSelectedPhotosForBulkAssign] = useState([]); // Multi-select for bulk assign
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [assigningPhoto, setAssigningPhoto] = useState(false);
  const [deletingPhotos, setDeletingPhotos] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Photo upload handler (used by both drag-drop and file input)
  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      // Only accept images
      if (!files[i].type.startsWith('image/')) {
        toast.error(`${files[i].name} is not an image file`);
        continue;
      }
      formData.append('files', files[i]);
    }
    
    if (formData.getAll('files').length === 0) {
      toast.error('No valid image files selected');
      return;
    }
    
    setUploadingPhotos(true);
    try {
      toast.info(`Uploading ${formData.getAll('files').length} photo(s)...`);
      const response = await api.post('/admin/media-library/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.uploaded?.length > 0) {
        toast.success(`✅ Uploaded ${response.data.uploaded.length} photo(s)!`);
        loadPhotoGallery();
      }
      if (response.data.failed?.length > 0) {
        toast.error(`Failed to upload ${response.data.failed.length} file(s)`);
      }
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Function to load photo gallery data
  const loadPhotoGallery = async (append = false) => {
    setLoadingPhotos(true);
    try {
      const currentSkip = append ? unusedPhotos.length : 0;
      const [photosRes, mustangsRes] = await Promise.all([
        api.get(`/admin/unused-photos?skip=${currentSkip}&limit=30`),
        api.get('/admin/mustangs-needing-photos')
      ]);
      
      const newPhotos = photosRes.data.unused_photos || [];
      if (append) {
        setUnusedPhotos(prev => [...prev, ...newPhotos]);
      } else {
        setUnusedPhotos(newPhotos);
      }
      setPhotoGalleryTotal(photosRes.data.total || 0);
      setPhotoGalleryHasMore(photosRes.data.has_more || false);
      setMustangsNeedingPhotos(mustangsRes.data.mustangs || []);
      
      if (!append) {
        setSelectedPhotosForBulkAssign([]);
      }
    } catch (error) {
      console.error('Photo gallery error:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Toggle photo selection for bulk assign
  const togglePhotoSelection = (photo) => {
    setSelectedPhotosForBulkAssign(prev => {
      const isSelected = prev.some(p => p.filename === photo.filename);
      if (isSelected) {
        return prev.filter(p => p.filename !== photo.filename);
      } else {
        return [...prev, photo];
      }
    });
  };

  // Select all unused photos
  const selectAllPhotos = () => {
    setSelectedPhotosForBulkAssign([...unusedPhotos]);
  };

  // Deselect all photos
  const deselectAllPhotos = () => {
    setSelectedPhotosForBulkAssign([]);
  };

  // Handle bulk delete photos
  const handleBulkDeletePhotos = async () => {
    if (selectedPhotosForBulkAssign.length === 0) {
      toast.error('No photos selected');
      return;
    }
    
    if (deletingPhotos) return;
    
    setDeletingPhotos(true);
    toast.info(`Deleting ${selectedPhotosForBulkAssign.length} photo(s)...`);
    
    try {
      const filenames = selectedPhotosForBulkAssign.map(p => p.filename);
      const response = await api.post('/admin/media-library/bulk-delete', { filenames });
      
      if (response.data.deleted > 0) {
        toast.success(`Deleted ${response.data.deleted} photo(s)`);
      }
      if (response.data.failed > 0) {
        toast.error(`Failed to delete ${response.data.failed} photo(s)`);
      }
      setSelectedPhotosForBulkAssign([]);
      loadPhotoGallery();
    } catch (error) {
      console.error('[BULK DELETE] Error:', error);
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPhotos(false);
    }
  };

  // Handle bulk assign photos to a mustang
  const handleBulkAssignToMustang = async (mustangId, mustangName) => {
    if (selectedPhotosForBulkAssign.length === 0) {
      toast.error('No photos selected');
      return;
    }
    
    setAssigningPhoto(true);
    try {
      const photoUrls = selectedPhotosForBulkAssign.map(p => p.url);
      await api.post('/admin/assign-photos-to-mustang', {
        mustang_id: mustangId,
        photo_urls: photoUrls
      });
      toast.success(`${selectedPhotosForBulkAssign.length} photo(s) added to ${mustangName}!`);
      setShowBulkAssignModal(false);
      setSelectedPhotosForBulkAssign([]);
      // Refresh data
      fetchVirtualMustangs();
      loadPhotoGallery();
    } catch (error) {
      toast.error('Failed to assign photos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAssigningPhoto(false);
    }
  };

  useEffect(() => {
    if (user && user.is_admin) {
      fetchModules();
      fetchUsers();
      fetchResources();
      fetchTrainers();
      fetchPricingPlans();
      fetchAdoptionSections();
      fetchDashboardModules();
      fetchVirtualCorralSettings();
      fetchRanchGameSettings();
      fetchVirtualMustangs();
      fetchAppSettings();
      fetchRanchBlocks();
      fetchCoupons();
      fetchMercantileItems();
      fetchMercantileCategories();
      fetchCoinActivities();
      loadPhotoGallery(); // Auto-load photo gallery
      fetchHerdSettings();
      fetchMediaLibrary();
    }
  }, [user]);

  // Media Library functions
  const fetchMediaLibrary = async (page = 0) => {
    setMediaLoading(true);
    try {
      // Load 100 items at a time for faster browsing
      const response = await api.get(`/admin/media-library?skip=${page * 100}&limit=100`);
      setMediaLibrary(response.data);
      setMediaPage(page);
    } catch (error) {
      console.error('Error fetching media library:', error);
      toast.error('Failed to load media library');
    } finally {
      setMediaLoading(false);
    }
  };

  // Fetch community members
  const fetchCommunityMembers = async () => {
    setCommunityLoading(true);
    try {
      const response = await api.get('/admin/community-members');
      setCommunityMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching community members:', error);
      toast.error('Failed to load community members');
    } finally {
      setCommunityLoading(false);
    }
  };

  // Delete community member
  const handleDeleteCommunityMember = async (userId, displayName) => {
    console.log('[COMMUNITY] Delete requested for:', userId, displayName);
    
    if (!window.confirm(`Are you sure you want to delete "${displayName}" from the community? This will remove their profile, messages, and community data. This cannot be undone!`)) {
      console.log('[COMMUNITY] Delete cancelled');
      return;
    }

    try {
      console.log('[COMMUNITY] Sending delete request...');
      const response = await api.delete(`/admin/community-members/${userId}`);
      console.log('[COMMUNITY] Delete response:', response.data);
      toast.success(`"${displayName}" has been removed from the community`);
      fetchCommunityMembers();
    } catch (error) {
      console.error('[COMMUNITY] Error deleting:', error);
      console.error('[COMMUNITY] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete community member');
    }
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      toast.info(`Uploading ${files.length} file(s)...`);
      
      const response = await api.post('/admin/media-library/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      if (response.data.total_uploaded > 0) {
        toast.success(`${response.data.total_uploaded} file(s) uploaded successfully!`);
        fetchMediaLibrary(mediaPage);
      }
      
      if (response.data.total_failed > 0) {
        toast.error(`${response.data.total_failed} file(s) failed to upload`);
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadingMedia(false);
      e.target.value = '';
    }
  };

  const handleDeleteMedia = async (filename) => {
    if (!window.confirm('Delete this media file? This cannot be undone.')) return;
    
    try {
      await api.delete(`/admin/media-library/${filename}`);
      toast.success('Media file deleted');
      fetchMediaLibrary(mediaPage);
      setSelectedMedia(selectedMedia.filter(f => f !== filename));
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media file');
    }
  };

  const handleDeleteSelectedMedia = async () => {
    if (selectedMedia.length === 0) return;
    if (!window.confirm(`Delete ${selectedMedia.length} selected file(s)? This cannot be undone.`)) return;
    
    let deleted = 0;
    for (const filename of selectedMedia) {
      try {
        await api.delete(`/admin/media-library/${filename}`);
        deleted++;
      } catch (error) {
        console.error(`Failed to delete ${filename}:`, error);
      }
    }
    
    toast.success(`${deleted} file(s) deleted`);
    fetchMediaLibrary(mediaPage);
    setSelectedMedia([]);
  };

  const toggleMediaSelection = (filename) => {
    if (selectedMedia.includes(filename)) {
      setSelectedMedia(selectedMedia.filter(f => f !== filename));
    } else {
      setSelectedMedia([...selectedMedia, filename]);
    }
  };

  const copyMediaUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard!');
  };


  // Herd Settings functions
  const fetchHerdSettings = async () => {
    try {
      const response = await api.get('/admin/herd-settings');
      setHerdSettings(response.data);
    } catch (error) {
      console.error('Error fetching herd settings:', error);
    }
  };

  const handleSaveHerdSettings = async (type, categories) => {
    try {
      const updateData = type === 'daily_care' 
        ? { daily_care_categories: categories }
        : { training_categories: categories };
      
      const response = await api.put('/admin/herd-settings', updateData);
      setHerdSettings(response.data);
      toast.success(`${type === 'daily_care' ? 'Daily Care' : 'Training'} categories saved!`);
    } catch (error) {
      console.error('Error saving herd settings:', error);
      toast.error('Failed to save herd settings');
    }
  };

  const handleResetHerdSettings = async () => {
    if (!window.confirm('Reset all herd settings to defaults? This cannot be undone.')) return;
    
    try {
      const response = await api.post('/admin/herd-settings/reset');
      setHerdSettings(response.data.settings);
      toast.success('Herd settings reset to defaults');
    } catch (error) {
      console.error('Error resetting herd settings:', error);
      toast.error('Failed to reset herd settings');
    }
  };

  const handleAddDailyCareCategory = () => {
    setEditingDailyCareCategory(null);
    setDailyCareForm({ key: '', label: '', icon_url: '', items: [{ key: '', label: '' }] });
    setShowDailyCareDialog(true);
  };

  const handleEditDailyCareCategory = (category, index) => {
    setEditingDailyCareCategory(index);
    setDailyCareForm({ ...category, items: category.items.length > 0 ? category.items : [{ key: '', label: '' }] });
    setShowDailyCareDialog(true);
  };

  const handleSaveDailyCareCategory = () => {
    if (!dailyCareForm.key || !dailyCareForm.label) {
      toast.error('Please enter category key and label');
      return;
    }
    
    const validItems = dailyCareForm.items.filter(item => item.key && item.label);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    const updatedCategory = { ...dailyCareForm, items: validItems };
    let updatedCategories = [...(herdSettings?.daily_care_categories || [])];
    
    if (editingDailyCareCategory !== null) {
      updatedCategories[editingDailyCareCategory] = updatedCategory;
    } else {
      updatedCategories.push(updatedCategory);
    }
    
    handleSaveHerdSettings('daily_care', updatedCategories);
    setShowDailyCareDialog(false);
  };

  const handleDeleteDailyCareCategory = async (index) => {
    if (!window.confirm('Delete this category? This cannot be undone.')) return;
    
    const updatedCategories = herdSettings.daily_care_categories.filter((_, i) => i !== index);
    handleSaveHerdSettings('daily_care', updatedCategories);
  };

  const handleAddTrainingCategory = () => {
    setEditingTrainingCategory(null);
    setTrainingForm({ key: '', label: '', items: [{ key: '', label: '' }] });
    setShowTrainingDialog(true);
  };

  const handleEditTrainingCategory = (category, index) => {
    setEditingTrainingCategory(index);
    setTrainingForm({ ...category, items: category.items.length > 0 ? category.items : [{ key: '', label: '' }] });
    setShowTrainingDialog(true);
  };

  const handleSaveTrainingCategory = () => {
    if (!trainingForm.key || !trainingForm.label) {
      toast.error('Please enter category key and label');
      return;
    }
    
    const validItems = trainingForm.items.filter(item => item.key && item.label);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    const updatedCategory = { ...trainingForm, items: validItems };
    let updatedCategories = [...(herdSettings?.training_categories || [])];
    
    if (editingTrainingCategory !== null) {
      updatedCategories[editingTrainingCategory] = updatedCategory;
    } else {
      updatedCategories.push(updatedCategory);
    }
    
    handleSaveHerdSettings('training', updatedCategories);
    setShowTrainingDialog(false);
  };

  const handleDeleteTrainingCategory = async (index) => {
    if (!window.confirm('Delete this category? This cannot be undone.')) return;
    
    const updatedCategories = herdSettings.training_categories.filter((_, i) => i !== index);
    handleSaveHerdSettings('training', updatedCategories);
  };

  const handleHerdCategoryIconUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isValidImageType(file)) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingHerdIcon(true);
    try {
      const processedFile = await processImageForUpload(file, 8);
      const formData = new FormData();
      formData.append('file', processedFile);
      
      const response = await api.post('/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'daily_care') {
        setDailyCareForm({ ...dailyCareForm, icon_url: response.data.url });
      }
      toast.success('Icon uploaded!');
    } catch (error) {
      toast.error('Failed to upload icon');
    } finally {
      setUploadingHerdIcon(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await api.get('/admin/resources');
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const handleCreateResource = async () => {
    if (!resourceForm.title.trim() || !resourceForm.link.trim()) {
      toast.error('Please enter title and link');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.post('/admin/resources', resourceForm);
      toast.success('Resource created successfully!');
      setShowResourceDialog(false);
      setResourceForm({ title: '', description: '', link: '', category: 'general', order: 0 });
      fetchResources();
    } catch (error) {
      console.error('Error creating resource:', error);
      toast.error(error.response?.data?.detail || 'Failed to create resource');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateResource = async () => {
    if (!resourceForm.title.trim() || !resourceForm.link.trim()) {
      toast.error('Please enter title and link');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put(`/admin/resources/${editingResource.id}`, resourceForm);
      toast.success('Resource updated successfully!');
      setShowResourceDialog(false);
      setEditingResource(null);
      setResourceForm({ title: '', description: '', link: '', category: 'general', order: 0 });
      fetchResources();
    } catch (error) {
      console.error('Error updating resource:', error);
      toast.error('Failed to update resource');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await api.delete(`/admin/resources/${resourceId}`);
      toast.success('Resource deleted successfully!');
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    }
  };

  const openEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title,
      description: resource.description,
      link: resource.link,
      category: resource.category || 'general',
      order: resource.order
    });
    setShowResourceDialog(true);
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/quiz-modules');
      setModules(response.data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load quiz modules: ${errorMsg}`);
      setModules([]); // Set empty array to prevent further errors
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      console.log('Fetching users...');
      const response = await api.get('/admin/users');
      console.log('Users fetched:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  // Subscription Management functions
  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const response = await api.get('/admin/subscriptions');
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleGrantSubscription = async () => {
    if (!grantSubscriptionEmail) {
      toast.error('Please enter a user email');
      return;
    }
    try {
      const response = await api.post('/admin/subscriptions/grant', {
        user_email: grantSubscriptionEmail,
        days: grantSubscriptionDays
      });
      toast.success(`Subscription ${response.data.action} for ${response.data.user_email} until ${new Date(response.data.valid_until).toLocaleDateString()}`);
      setGrantSubscriptionEmail('');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant subscription');
    }
  };

  const handleExtendSubscription = async (userId, userEmail, days = 30) => {
    try {
      const response = await api.post('/admin/subscriptions/grant', {
        user_id: userId,
        days: days
      });
      toast.success(`Extended subscription for ${userEmail} by ${days} days`);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend subscription');
    }
  };

  const handleRevokeSubscription = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to revoke subscription for ${userEmail}?`)) return;
    try {
      await api.post('/admin/subscriptions/revoke', { user_id: userId });
      toast.success(`Revoked subscription for ${userEmail}`);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke subscription');
    }
  };

  const handleFixAllSubscriptions = async () => {
    try {
      const response = await api.post('/admin/subscriptions/fix-all');
      toast.success(`Fixed ${response.data.fixed} subscriptions`);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix subscriptions');
    }
  };

  // App Settings functions
  const fetchAppSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      setAppSettings(response.data);
    } catch (error) {
      console.error('Error fetching app settings:', error);
      toast.error('Failed to load app settings');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image size must be less than 8MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await api.put('/admin/settings', {
          logo_url: reader.result
        });
        setAppSettings(response.data.settings);
        toast.success('Logo updated successfully!');
      } catch (error) {
        console.error('Error updating logo:', error);
        toast.error('Failed to update logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    try {
      const response = await api.put('/admin/settings', {
        logo_url: null
      });
      setAppSettings(response.data.settings);
      toast.success('Logo removed successfully!');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  // Trainer management functions
  const fetchTrainers = async () => {
    try {
      const response = await api.get('/admin/trainers');
      setTrainers(response.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Failed to load trainers');
    }
  };

  const handleCreateTrainer = async () => {
    if (!trainerForm.name.trim() || !trainerForm.bio.trim()) {
      toast.error('Please enter trainer name and bio');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.post('/admin/trainers', trainerForm);
      toast.success('Trainer created successfully!');
      setShowTrainerDialog(false);
      setTrainerForm({ name: '', photo_url: '', bio: '', city: '', state: '', specialties: [], availability: 'Available', response_time: 'Within 24 hours' });
      fetchTrainers();
    } catch (error) {
      console.error('Error creating trainer:', error);
      toast.error(error.response?.data?.detail || 'Failed to create trainer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTrainer = async () => {
    if (!trainerForm.name.trim() || !trainerForm.bio.trim()) {
      toast.error('Please enter trainer name and bio');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put(`/admin/trainers/${editingTrainer.id}`, trainerForm);
      toast.success('Trainer updated successfully!');
      setShowTrainerDialog(false);
      setEditingTrainer(null);
      setTrainerForm({ name: '', photo_url: '', bio: '', city: '', state: '', specialties: [], availability: 'Available', response_time: 'Within 24 hours' });
      fetchTrainers();
    } catch (error) {
      console.error('Error updating trainer:', error);
      toast.error('Failed to update trainer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTrainer = async (trainerId) => {
    console.log('Delete trainer clicked, ID:', trainerId);
    
    // Use native confirm - this should work on iPad
    const confirmed = window.confirm('Are you sure you want to delete this trainer?');
    
    if (!confirmed) {
      console.log('User cancelled deletion');
      return;
    }

    console.log('User confirmed deletion, proceeding...');
    
    try {
      console.log('Sending DELETE request to:', `/admin/trainers/${trainerId}`);
      const response = await api.delete(`/admin/trainers/${trainerId}`);
      console.log('Delete response:', response);
      toast.success('Trainer deleted successfully!');
      await fetchTrainers();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete trainer';
      toast.error(`Failed to delete trainer: ${errorMessage}`);
    }
  };

  const openEditTrainer = (trainer) => {
    setEditingTrainer(trainer);
    setTrainerForm({
      name: trainer.name,
      photo_url: trainer.photo_url || '',
      bio: trainer.bio,
      city: trainer.city || '',
      state: trainer.state || '',
      specialties: trainer.specialties || [],
      availability: trainer.availability || 'Available',
      response_time: trainer.response_time || 'Within 24 hours'
    });
    setShowTrainerDialog(true);
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !trainerForm.specialties.includes(specialtyInput.trim())) {
      setTrainerForm({
        ...trainerForm,
        specialties: [...trainerForm.specialties, specialtyInput.trim()]
      });
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty) => {
    setTrainerForm({
      ...trainerForm,
      specialties: trainerForm.specialties.filter(s => s !== specialty)
    });
  };

  // Pricing management functions
  const fetchPricingPlans = async () => {
    try {
      const response = await api.get('/admin/pricing-plans');
      setPricingPlans(response.data);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      toast.error('Failed to load pricing plans');
    }
  };

  const handleCreatePlan = async () => {
    if (!planForm.plan_type || !planForm.name || !planForm.description || planForm.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.post('/admin/pricing-plans', planForm);
      toast.success('Pricing plan created successfully!');
      setShowPlanDialog(false);
      setPlanForm({ plan_type: '', name: '', description: '', price: 0, duration_days: null });
      fetchPricingPlans();
    } catch (error) {
      console.error('Error creating pricing plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pricing plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!planForm.name || !planForm.description || planForm.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put(`/admin/pricing-plans/${editingPlan.id}`, planForm);
      toast.success('Pricing plan updated successfully!');
      setShowPlanDialog(false);
      setEditingPlan(null);
      setPlanForm({ plan_type: '', name: '', description: '', price: 0, duration_days: null });
      fetchPricingPlans();
    } catch (error) {
      console.error('Error updating pricing plan:', error);
      toast.error('Failed to update pricing plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this pricing plan?')) {
      return;
    }

    try {
      await api.delete(`/admin/pricing-plans/${planId}`);
      toast.success('Pricing plan deleted successfully!');
      fetchPricingPlans();
    } catch (error) {
      console.error('Error deleting pricing plan:', error);
      toast.error('Failed to delete pricing plan');
    }
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      plan_type: plan.plan_type,
      name: plan.name,
      description: plan.description,
      price: plan.price,
      duration_days: plan.duration_days
    });
    setShowPlanDialog(true);
  };

  // Adoption guide management functions
  const fetchAdoptionSections = async () => {
    try {
      const response = await api.get('/admin/adoption-guide');
      setAdoptionSections(response.data);
    } catch (error) {
      console.error('Error fetching adoption sections:', error);
      toast.error('Failed to load adoption guide');
    }
  };

  const handleCreateSection = async () => {
    if (!sectionForm.title || !sectionForm.content) {
      toast.error('Please fill in title and content');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.post('/admin/adoption-guide', sectionForm);
      toast.success('Section created successfully!');
      setShowSectionDialog(false);
      setSectionForm({ title: '', content: '', order: 0, icon: '', icon_image_url: '' });
      fetchAdoptionSections();
    } catch (error) {
      console.error('Error creating section:', error);
      toast.error(error.response?.data?.detail || 'Failed to create section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSection = async () => {
    if (!sectionForm.title || !sectionForm.content) {
      toast.error('Please fill in title and content');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put(`/admin/adoption-guide/${editingSection.id}`, sectionForm);
      toast.success('Section updated successfully!');
      setShowSectionDialog(false);
      setEditingSection(null);
      setSectionForm({ title: '', content: '', order: 0, icon: '', icon_image_url: '' });
      fetchAdoptionSections();
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) {
      return;
    }

    try {
      await api.delete(`/admin/adoption-guide/${sectionId}`);
      toast.success('Section deleted successfully!');
      fetchAdoptionSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const openEditSection = (section) => {
    setEditingSection(section);
    setSectionForm({
      title: section.title,
      content: section.content,
      order: section.order,
      icon: section.icon || '',
      icon_image_url: section.icon_image_url || ''
    });
    setShowSectionDialog(true);
  };

  // Dashboard module management functions
  const fetchDashboardModules = async () => {
    try {
      const response = await api.get('/admin/dashboard-modules');
      setDashboardModules(response.data);
    } catch (error) {
      console.error('Error fetching dashboard modules:', error);
      toast.error('Failed to load dashboard modules');
    }
  };

  const handleUpdateDashModule = async () => {
    if (!dashModuleForm.title || !dashModuleForm.description) {
      toast.error('Please fill in title and description');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put(`/admin/dashboard-modules/${editingDashModule.id}`, dashModuleForm);
      toast.success('Dashboard module updated successfully!');
      setShowDashModuleDialog(false);
      setEditingDashModule(null);
      setDashModuleForm({ title: '', description: '', icon_name: '', icon_image_url: '', bg_color: '#6FCFD3' });
      fetchDashboardModules();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDashModule = (module) => {
    setEditingDashModule(module);
    setDashModuleForm({
      title: module.title,
      description: module.description,
      icon_name: module.icon_name || '',
      icon_image_url: module.icon_image_url || '',
      bg_color: module.bg_color || '#6FCFD3'
    });
    setShowDashModuleDialog(true);
  };

  const handleDashIconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('Icon image size must be less than 8MB');
      return;
    }

    try {
      setUploadingDashIcon(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store relative URL - will be made absolute when displayed
      const iconUrl = response.data.url;
      setDashModuleForm({
        ...dashModuleForm,
        icon_image_url: iconUrl,
        icon_name: ''
      });

      toast.success('Module icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading module icon:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingDashIcon(false);
    }
  };

  // Virtual Corral management functions
  const fetchVirtualCorralSettings = async () => {
    try {
      const response = await api.get('/virtual-corral/settings');
      setVirtualCorralSettings(response.data);
      setVcSettingsForm({
        monthly_price: response.data.monthly_price || 9.99,
        max_mustangs_per_event: response.data.max_mustangs_per_event || 4
      });
    } catch (error) {
      console.error('Error fetching Virtual Corral settings:', error);
    }
  };

  const handleUpdateVcSettings = async () => {
    if (vcSettingsForm.monthly_price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    if (vcSettingsForm.max_mustangs_per_event < 1 || vcSettingsForm.max_mustangs_per_event > 10) {
      toast.error('Max mustangs must be between 1 and 10');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put('/admin/virtual-corral/settings', vcSettingsForm);
      toast.success('Virtual Corral settings updated successfully!');
      fetchVirtualCorralSettings();
    } catch (error) {
      console.error('Error updating Virtual Corral settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchRanchGameSettings = async () => {
    try {
      const response = await api.get('/ranch-game/settings');
      setRanchGameSettings(response.data);
      setRanchGameForm({
        game_title: response.data.game_title || 'My Ranch Game',
        game_description: response.data.game_description || 'Build and manage your virtual ranch with interactive gameplay',
        is_enabled: response.data.is_enabled || false,
        coming_soon_message: response.data.coming_soon_message || 'The Ranch Game is currently under development and will be available soon!',
        feature_image_url: response.data.feature_image_url || '',
        release_date: response.data.release_date || ''
      });
    } catch (error) {
      console.error('Error fetching Ranch Game settings:', error);
    }
  };

  const handleUpdateRanchGameSettings = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.put('/admin/ranch-game/settings', ranchGameForm);
      toast.success('Ranch Game settings updated successfully!');
      fetchRanchGameSettings();
    } catch (error) {
      console.error('Error updating Ranch Game settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRanchImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setUploadingRanchImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setRanchGameForm({ ...ranchGameForm, feature_image_url: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingRanchImage(false);
    }
  };

  const handleIconImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 8MB for icons)
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Icon image size must be less than 8MB');
      return;
    }

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store relative URL - will be made absolute when displayed
      const iconUrl = response.data.url;
      setSectionForm({
        ...sectionForm,
        icon_image_url: iconUrl,
        icon: '' // Clear pre-made icon when custom icon is uploaded
      });

      toast.success('Icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleTrainerPhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (including HEIC/HEIF)
    if (!isValidImageType(file)) {
      toast.error('Please upload an image file (JPG, PNG, GIF, WEBP, or HEIC)');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Process image (convert HEIC if needed, compress if over 8MB)
      toast.info('Processing image...');
      const processedFile = await processImageForUpload(file, 8, (progress) => {
        if (progress.stage === 'converting') {
          toast.info('Converting HEIC to JPEG...');
        } else if (progress.stage === 'compressing' && progress.progress === 0) {
          toast.info('Compressing image...');
        }
      });
      
      const formData = new FormData();
      formData.append('file', processedFile);

      const response = await api.post('/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store relative URL - will be made absolute when displayed
      const photoUrl = response.data.url;
      setTrainerForm({
        ...trainerForm,
        photo_url: photoUrl
      });

      toast.success(`Photo uploaded! (${formatFileSize(processedFile.size)})`);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCreateModule = async () => {
    // Validation
    if (!moduleForm.title || !moduleForm.title.trim()) {
      toast.error('Please enter a module title');
      return;
    }
    if (!moduleForm.description || !moduleForm.description.trim()) {
      toast.error('Please enter a module description');
      return;
    }

    if (isSubmitting) return; // Prevent double submission

    try {
      setIsSubmitting(true);
      console.log('Creating module with data:', moduleForm);
      const response = await api.post('/admin/quiz-modules', moduleForm);
      console.log('Module created successfully:', response.data);
      toast.success('Module created successfully!');
      setShowModuleDialog(false);
      setModuleForm({ title: '', description: '', order: 0 });
      fetchModules();
    } catch (error) {
      console.error('Error creating module:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateModule = async () => {
    try {
      await api.put(`/admin/quiz-modules/${editingModule.id}`, moduleForm);
      toast.success('Module updated successfully!');
      setShowModuleDialog(false);
      setEditingModule(null);
      setModuleForm({ title: '', description: '', order: 0 });
      fetchModules();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module? This will delete all questions in it.')) {
      return;
    }

    try {
      await api.delete(`/admin/quiz-modules/${moduleId}`);
      toast.success('Module deleted successfully!');
      fetchModules();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module');
    }
  };

  const handleSeedAdoptionQuestions = async () => {
    if (seedingAdoption) return;
    
    try {
      setSeedingAdoption(true);
      const response = await api.post('/admin/seed-adoption-questions');
      const data = response.data;
      
      if (data.added > 0) {
        toast.success(`Added ${data.added} new questions! Total: ${data.total}`);
      } else {
        toast.info('All questions already exist in the module');
      }
      
      // Refresh the modules list
      fetchModules();
    } catch (error) {
      console.error('Error seeding adoption questions:', error);
      toast.error('Failed to seed adoption questions');
    } finally {
      setSeedingAdoption(false);
    }
  };

  const handleSeedAllModules = async () => {
    if (seedingModules) return;
    
    try {
      setSeedingModules(true);
      const response = await api.post('/admin/seed-quiz-modules');
      const data = response.data;
      
      if (data.created > 0) {
        toast.success(`Created ${data.created} new quiz modules!`);
      } else {
        toast.info('All quiz modules already exist');
      }
      
      // Refresh the modules list
      fetchModules();
    } catch (error) {
      console.error('Error seeding quiz modules:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to seed quiz modules: ${errorMsg}`);
    } finally {
      setSeedingModules(false);
    }
  };

  const handleSeedTransportQuestions = async () => {
    if (seedingTransport) return;
    
    try {
      setSeedingTransport(true);
      const response = await api.post('/admin/seed-transport-questions');
      const data = response.data;
      
      if (data.added > 0) {
        toast.success(`Added ${data.added} new Transport questions! Total: ${data.total}`);
      } else {
        toast.info('All Transport questions already exist in the module');
      }
      
      // Refresh the modules list
      fetchModules();
    } catch (error) {
      console.error('Error seeding transport questions:', error);
      toast.error('Failed to seed transport questions');
    } finally {
      setSeedingTransport(false);
    }
  };

  const handleMigrateMedia = async () => {
    if (migratingMedia) return;
    
    try {
      setMigratingMedia(true);
      const response = await api.post('/admin/migrate-media-to-db');
      const data = response.data;
      
      if (data.migrated > 0) {
        toast.success(`Migrated ${data.migrated} files to database! Skipped: ${data.skipped}`);
      } else if (data.skipped > 0) {
        toast.info(`All ${data.skipped} files already in database`);
      } else {
        toast.info('No files to migrate');
      }
      
      if (data.errors && data.errors.length > 0) {
        toast.error(`${data.errors.length} files failed to migrate`);
      }
    } catch (error) {
      console.error('Error migrating media:', error);
      toast.error('Failed to migrate media files');
    } finally {
      setMigratingMedia(false);
    }
  };

  const handleAddQuestion = async () => {
    try {
      // Save scroll position before any state changes
      const scrollPosition = window.scrollY;
      
      await api.post(`/admin/quiz-modules/${selectedModule.id}/questions`, questionForm);
      toast.success('Question added successfully!');
      setShowQuestionDialog(false);
      setQuestionForm({
        question: '',
        options: [
          { text: '', photo_url: '' },
          { text: '', photo_url: '' }
        ],
        correct: [0],
        explanation: '',
        order: 0,
        photo_url: ''
      });
      await fetchModules();
      
      // Restore scroll position after data is loaded - use multiple attempts
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
        setTimeout(() => window.scrollTo(0, scrollPosition), 50);
        setTimeout(() => window.scrollTo(0, scrollPosition), 150);
        setTimeout(() => window.scrollTo(0, scrollPosition), 300);
      });
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question');
    }
  };

  const handleUpdateQuestion = async () => {
    // Save scroll position BEFORE any state changes
    const scrollY = window.scrollY;
    
    console.log('[UPDATE QUESTION] Starting update...', {
      selectedModule: selectedModule?.id,
      editingQuestion: editingQuestion?.id,
      questionForm
    });
    
    if (!selectedModule || !editingQuestion) {
      console.error('[UPDATE QUESTION] Missing selectedModule or editingQuestion');
      toast.error('Error: Missing module or question data');
      return;
    }
    
    try {
      const targetModuleId = getModuleIdFromSection(questionForm.section);
      const currentModuleId = selectedModule.id;
      
      console.log('[UPDATE QUESTION] Target module:', targetModuleId, 'Current module:', currentModuleId);
      
      if (targetModuleId && targetModuleId !== currentModuleId) {
        // Question is being moved to a different module
        console.log(`Moving question from ${currentModuleId} to ${targetModuleId}`);
        
        // First, delete the question from the current module
        await fetch(`${window.location.origin}/api/admin/quiz-modules/${currentModuleId}/questions/${editingQuestion.id}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        // Then, add it to the target module
        await api.post(`/admin/quiz-modules/${targetModuleId}/questions`, {
          ...questionForm,
          id: editingQuestion.id // Keep the same ID
        });
        
        toast.success('Question moved to new module successfully!');
        // For module moves, we need to refetch
        await fetchModules();
      } else {
        // Question staying in the same module, just update it
        console.log('[UPDATE QUESTION] Sending PUT request to:', `/admin/quiz-modules/${selectedModule.id}/questions/${editingQuestion.id}`);
        console.log('[UPDATE QUESTION] Data:', JSON.stringify(questionForm, null, 2));
        
        const response = await api.put(
          `/admin/quiz-modules/${selectedModule.id}/questions/${editingQuestion.id}`,
          questionForm
        );
        
        console.log('[UPDATE QUESTION] Response:', response.data);
        toast.success('Question updated successfully!');
        
        // Update local state directly instead of refetching (prevents scroll reset)
        setModules(prevModules => {
          return prevModules.map(module => {
            if (module.id === selectedModule.id) {
              return {
                ...module,
                questions: module.questions.map(q => 
                  q.id === editingQuestion.id 
                    ? { ...q, ...questionForm }
                    : q
                )
              };
            }
            return module;
          });
        });
      }
      
      // Close dialog and reset form
      setShowQuestionDialog(false);
      setEditingQuestion(null);
      setQuestionForm({
        question: '',
        options: [
          { text: '', photo_url: '' },
          { text: '', photo_url: '' }
        ],
        correct: [0],
        explanation: '',
        order: 0,
        section: 'adoption___application',
        photo_url: ''
      });
      
      // Restore scroll position after dialog closes
      setTimeout(() => { window.scrollTo(0, scrollY); }, 0);
      setTimeout(() => { window.scrollTo(0, scrollY); }, 100);
      setTimeout(() => { window.scrollTo(0, scrollY); }, 200);
      setTimeout(() => { window.scrollTo(0, scrollY); }, 400);
    } catch (error) {
      console.error('Error updating question:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Format error message properly
      let errorMsg = 'Failed to update question';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else {
          errorMsg = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    }
  };

  const handleDeleteQuestion = async (moduleId, questionId) => {
    console.log('🗑️ DELETE QUESTION CLICKED!', { moduleId, questionId });
    
    const confirmed = window.confirm('Are you sure you want to delete this question?');
    console.log('User confirmation:', confirmed);
    
    if (!confirmed) {
      console.log('Delete cancelled by user');
      return;
    }

    try {
      console.log('🔥 Attempting to delete question...');
      const response = await api.delete(`/admin/quiz-modules/${moduleId}/questions/${questionId}`);
      console.log('✅ Delete response:', response);
      toast.success('Question deleted successfully!');
      
      console.log('🔄 Refreshing modules...');
      await fetchModules();
      console.log('✅ Modules refreshed');
      
    } catch (error) {
      console.error('❌ Error deleting question:', error);
      toast.error(`Failed to delete question: ${error.message}`);
    }
  };

  const handleDuplicateQuestion = async (moduleId, question) => {
    try {
      // Create a copy of the question with a new ID
      const duplicatedQuestion = {
        question: question.question + ' (Copy)',
        options: [...question.options],
        correct: question.correct,
        explanation: question.explanation || ''
      };
      
      const response = await api.post(`/admin/quiz-modules/${moduleId}/questions`, duplicatedQuestion);
      toast.success('Question duplicated successfully!');
      await fetchModules();
    } catch (error) {
      console.error('Error duplicating question:', error);
      toast.error('Failed to duplicate question');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    console.log('Toggle admin for user:', userId, 'Current status:', currentStatus);
    try {
      const response = await api.put(`/admin/users/${userId}/admin-status`, { is_admin: !currentStatus });
      console.log('Toggle admin response:', response.data);
      toast.success(`Admin access ${!currentStatus ? 'granted' : 'revoked'} successfully!`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating admin status:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    console.log('Delete user requested:', userId, userName);
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will also delete all their data (mustangs, progress, subscriptions, etc.). This action cannot be undone!`)) {
      console.log('Delete cancelled by user');
      return;
    }

    try {
      console.log('Deleting user:', userId);
      const response = await api.delete(`/admin/users/${userId}`);
      console.log('Delete response:', response.data);
      toast.success(`User "${userName}" deleted successfully!`);
      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting user:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    console.log('Creating new user:', createUserForm);
    
    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await api.post('/admin/users/create', createUserForm);
      console.log('Create user response:', response.data);
      toast.success(response.data.message || 'User created successfully!');
      
      // Reset form and close dialog
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        is_admin: true
      });
      setShowCreateUserDialog(false);
      
      // Refresh user list
      await fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Virtual Mustang management functions
  const fetchVirtualMustangs = async () => {
    try {
      const response = await api.get('/admin/virtual-mustangs');
      setVirtualMustangs(response.data);
    } catch (error) {
      console.error('Error fetching virtual mustangs:', error);
      toast.error('Failed to load virtual mustangs');
    }
  };

  const handleVirtualMustangPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    e.target.value = ''; // Clear input immediately
    setUploadingMustangPhoto(true);
    toast.info(`Uploading ${files.length} photo(s)...`);

    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-media`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || ''}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.url) {
            uploadedUrls.push(data.url);
          }
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    if (uploadedUrls.length > 0) {
      setVirtualMustangForm(prev => ({
        ...prev,
        photo_url: prev.photo_urls?.length ? prev.photo_url : uploadedUrls[0],
        photo_urls: [...(prev.photo_urls || []), ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} photo(s) uploaded!`);
    } else {
      toast.error('Upload failed');
    }
    
    setUploadingMustangPhoto(false);
  };

  const handleRemoveMustangPhoto = (indexToRemove) => {
    const currentUrls = virtualMustangForm.photo_urls || [];
    const newPhotoUrls = currentUrls.filter((_, index) => index !== indexToRemove);
    
    setVirtualMustangForm({
      ...virtualMustangForm,
      photo_url: newPhotoUrls.length > 0 ? newPhotoUrls[0] : '',
      photo_urls: newPhotoUrls
    });
    toast.success('Photo removed');
  };

  const handleSetPrimaryMustangPhoto = (indexToSetPrimary) => {
    const currentUrls = virtualMustangForm.photo_urls || [];
    if (indexToSetPrimary >= 0 && indexToSetPrimary < currentUrls.length) {
      const newPhotoUrls = [
        currentUrls[indexToSetPrimary],
        ...currentUrls.filter((_, index) => index !== indexToSetPrimary)
      ];
      
      setVirtualMustangForm({
        ...virtualMustangForm,
        photo_url: newPhotoUrls[0],
        photo_urls: newPhotoUrls
      });
      toast.success('Primary photo updated');
    }
  };

  // Quick photo upload for existing mustangs (from the card grid)
  const handleQuickPhotoUpload = async (e, mustangId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setQuickUploadMustangId(mustangId);
    const uploadedUrls = [];
    const mustang = virtualMustangs.find(m => m.id === mustangId);

    toast.info(`Processing ${files.length} photo(s) for ${mustang?.name || 'mustang'}...`);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileSizeMB = file.size / (1024 * 1024);
      
      try {
        // Process image (convert HEIC if needed, compress)
        let processedFile = file;
        try {
          console.log(`[${i + 1}/${files.length}] Processing ${file.name} (${fileSizeMB.toFixed(1)}MB)...`);
          processedFile = await processImageForUpload(file, 4);
          console.log(`[${i + 1}/${files.length}] Processed size: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
        } catch (processError) {
          console.error('Image processing failed:', processError);
          processedFile = file;
        }
        
        // Check if processed file is still too large
        if (processedFile.size > 8 * 1024 * 1024) {
          throw new Error(`File too large after compression (${(processedFile.size / 1024 / 1024).toFixed(1)}MB)`);
        }
        
        const formData = new FormData();
        formData.append('file', processedFile);
        
        const response = await api.post('/upload-media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });

        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
          toast.success(`[${i + 1}/${files.length}] Uploaded!`, { duration: 2000 });
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        let errorMsg = error.message || 'Unknown error';
        if (error.code === 'ERR_NETWORK' || errorMsg.includes('Network Error')) {
          errorMsg = 'Network error - try a smaller image';
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        }
        toast.error(`Failed: ${file.name} - ${errorMsg}`);
      }
    }

    if (uploadedUrls.length > 0) {
      try {
        // Get current mustang data
        const currentUrls = mustang?.photo_urls || [];
        const newPhotoUrls = [...uploadedUrls, ...currentUrls];

        // Update the mustang with new photos
        await api.put(`/admin/virtual-mustangs/${mustangId}`, {
          photo_url: newPhotoUrls[0],
          photo_urls: newPhotoUrls
        });

        toast.success(`Added ${uploadedUrls.length} photo(s) to ${mustang?.name || 'mustang'}!`);
        fetchVirtualMustangs(); // Refresh the list
      } catch (updateError) {
        console.error('Failed to update mustang:', updateError);
        toast.error(`Failed to save photos: ${updateError.response?.data?.detail || updateError.message}`);
      }
    }

    setQuickUploadMustangId(null);
    e.target.value = '';
  };

  const handleCreateVirtualMustang = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.post('/admin/virtual-mustangs', {
        ...virtualMustangForm,
        age: parseInt(virtualMustangForm.age)
      });
      
      toast.success('Virtual mustang created successfully!');
      setShowVirtualMustangDialog(false);
      setVirtualMustangForm({
        name: '',
        age: 0,
        gender: '',
        color: '',
        hma: '',
        height: '',
        disposition: '',
        freezemark: '',
        photo_url: '',
        photo_urls: [],
        adoption_method: 'olc',
        image_position: 'center center'
      });
      fetchVirtualMustangs();
    } catch (error) {
      console.error('Error creating virtual mustang:', error);
      toast.error(error.response?.data?.detail || 'Failed to create virtual mustang');
    }
  };

  const handleUpdateVirtualMustang = async (e) => {
    e.preventDefault();
    
    try {
      const updatePayload = {
        ...virtualMustangForm,
        age: parseInt(virtualMustangForm.age)
      };
      console.log('Updating mustang with payload:', updatePayload);
      
      const response = await api.put(`/admin/virtual-mustangs/${editingVirtualMustang.id}`, updatePayload);
      
      console.log('Mustang updated:', response.data);
      toast.success('Virtual mustang updated successfully!');
      setShowVirtualMustangDialog(false);
      setEditingVirtualMustang(null);
      setVirtualMustangForm({
        name: '',
        age: 0,
        gender: '',
        color: '',
        hma: '',
        height: '',
        disposition: '',
        freezemark: '',
        photo_url: '',
        photo_urls: [],
        adoption_method: 'olc',
        image_position: 'center center'
      });
      await fetchVirtualMustangs();
    } catch (error) {
      console.error('Error updating virtual mustang:', error);
      toast.error(error.response?.data?.detail || 'Failed to update virtual mustang');
    }
  };

  const handleDeleteVirtualMustang = async (mustangId, mustangName) => {
    // Skip confirmation on mobile - just delete directly
    try {
      await api.delete(`/admin/virtual-mustangs/${mustangId}`);
      toast.success(`"${mustangName}" deleted successfully!`);
      fetchVirtualMustangs();
    } catch (error) {
      console.error('Error deleting virtual mustang:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete virtual mustang');
    }
  };

  const openEditVirtualMustang = (mustang) => {
    setEditingVirtualMustang(mustang);
    // Ensure photo_urls is populated from photo_url if it doesn't exist
    const photoUrls = mustang.photo_urls || (mustang.photo_url ? [mustang.photo_url] : []);
    // Normalize old single-word positions to new format
    let imagePos = mustang.image_position || 'center center';
    if (imagePos === 'top') imagePos = 'center top';
    if (imagePos === 'center' && !imagePos.includes(' ')) imagePos = 'center center';
    if (imagePos === 'bottom') imagePos = 'center bottom';
    
    setVirtualMustangForm({
      name: mustang.name,
      age: mustang.age,
      gender: mustang.gender,
      color: mustang.color,
      hma: mustang.hma,
      height: mustang.height,
      disposition: mustang.disposition,
      freezemark: mustang.freezemark || '',
      photo_url: mustang.photo_url || (photoUrls.length > 0 ? photoUrls[0] : ''),
      photo_urls: photoUrls,
      adoption_method: mustang.adoption_method,
      image_position: imagePos
    });
    setShowVirtualMustangDialog(true);
  };

  const handleGenerateOutline = async (moduleId) => {
    try {
      await api.post(`/admin/quiz-modules/${moduleId}/generate-outline`);
      toast.success('Outline generated from questions!');
      fetchModules();
    } catch (error) {
      console.error('Error generating outline:', error);
      toast.error('Failed to generate outline');
    }
  };

  // Ranch Blocks Management Functions
  const fetchRanchBlocks = async () => {
    try {
      const response = await api.get('/admin/ranch-blocks');
      setRanchBlocks(response.data);
    } catch (error) {
      console.error('Error fetching ranch blocks:', error);
      toast.error('Failed to load ranch blocks');
    }
  };

  const handleCreateRanchBlock = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/ranch-blocks', ranchBlockForm);
      toast.success('Ranch block created!');
      setShowRanchBlockDialog(false);
      setRanchBlockForm({
        title: '',
        html_content: '',
        css_content: '',
        js_content: '',
        assets: [],
        position: 'main',
        is_active: true,
        order: 0
      });
      fetchRanchBlocks();
    } catch (error) {
      console.error('Error creating ranch block:', error);
      toast.error('Failed to create ranch block');
    }
  };

  const handleUpdateRanchBlock = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/ranch-blocks/${editingRanchBlock.id}`, ranchBlockForm);
      toast.success('Ranch block updated!');
      setShowRanchBlockDialog(false);
      setEditingRanchBlock(null);
      setRanchBlockForm({
        title: '',
        html_content: '',
        css_content: '',
        js_content: '',
        assets: [],
        position: 'main',
        is_active: true,
        order: 0
      });
      fetchRanchBlocks();
    } catch (error) {
      console.error('Error updating ranch block:', error);
      toast.error('Failed to update ranch block');
    }
  };

  const handleDeleteRanchBlock = async (blockId, blockTitle) => {
    if (!window.confirm(`Delete "${blockTitle}"?`)) return;
    
    try {
      await api.delete(`/admin/ranch-blocks/${blockId}`);
      toast.success('Ranch block deleted');
      fetchRanchBlocks();
    } catch (error) {
      console.error('Error deleting ranch block:', error);
      toast.error('Failed to delete ranch block');
    }
  };

  const openEditRanchBlock = (block) => {
    setEditingRanchBlock(block);
    setRanchBlockForm({
      title: block.title,
      html_content: block.html_content,
      css_content: block.css_content || '',
      js_content: block.js_content,
      assets: block.assets || [],
      position: block.position,
      is_active: block.is_active,
      order: block.order
    });
    setShowRanchBlockDialog(true);
  };


  const handleSaveOutline = async () => {
    try {
      await api.put(`/admin/quiz-modules/${selectedModule.id}`, {
        outline_content: outlineContent
      });
      toast.success('Study guide updated successfully!');
      setShowOutlineDialog(false);
      fetchModules();
    } catch (error) {
      console.error('Error saving outline:', error);
      toast.error('Failed to save study guide');
    }
  };

  const openEditOutline = (module) => {
    setSelectedModule(module);
    setOutlineContent(module.outline_content || []);
    setShowOutlineDialog(true);
  };

  const addOutlineItem = () => {
    setOutlineContent([...outlineContent, '']);
  };

  const updateOutlineItem = (index, value) => {
    const newContent = [...outlineContent];
    newContent[index] = value;
    setOutlineContent(newContent);
  };

  const removeOutlineItem = (index) => {
    const newContent = outlineContent.filter((_, i) => i !== index);
    setOutlineContent(newContent);
  };

  const openEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title,
      description: module.description,
      order: module.order
    });
    setShowModuleDialog(true);
  };

  // Coupon Code Functions
  const fetchCoupons = async () => {
    try {
      const response = await api.get('/admin/coupons');
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await api.post('/admin/coupons', couponForm);
      toast.success('Coupon code created successfully!');
      setShowCouponDialog(false);
      setCouponForm({
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        description: '',
        max_uses: null,
        expiration_date: ''
      });
      fetchCoupons();
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon code?')) return;

    try {
      await api.delete(`/admin/coupons/${couponId}`);
      toast.success('Coupon deleted successfully!');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    }
  };


  // ============= MERCANTILE FUNCTIONS =============
  
  const fetchMercantileItems = async () => {
    try {
      const response = await api.get('/admin/mercantile/items');
      setMercantileItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching mercantile items:', error);
    }
  };

  const fetchMercantileCategories = async () => {
    try {
      const response = await api.get('/admin/mercantile/categories');
      setMercantileCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSyncCategories = async () => {
    try {
      await api.post('/admin/mercantile/categories/sync');
      toast.success('Categories synced from items!');
      fetchMercantileCategories();
    } catch (error) {
      toast.error('Failed to sync categories');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/admin/mercantile/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated!');
      } else {
        await api.post('/admin/mercantile/categories', categoryForm);
        toast.success('Category created!');
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', display_name: '' });
      fetchMercantileCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Delete this category? Items must be moved first.')) return;
    try {
      await api.delete(`/admin/mercantile/categories/${categoryId}`);
      toast.success('Category deleted!');
      fetchMercantileCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleCreateMercantileItem = async () => {
    try {
      if (editingMercantileItem) {
        // Update existing item
        await api.put(`/admin/mercantile/items/${editingMercantileItem.id}`, {
          name: mercantileForm.name,
          description: mercantileForm.description,
          price: parseInt(mercantileForm.price),
          category: mercantileForm.category,
          subcategory: mercantileForm.subcategory || null,
          image_url: mercantileForm.image_url || null,
          item_type: mercantileForm.item_type || 'inventory',
          inventory_key: mercantileForm.inventory_key || null,
          ranch_asset_key: mercantileForm.ranch_asset_key || null,
          stock: mercantileForm.stock ? parseInt(mercantileForm.stock) : -1
        });
        toast.success('Mercantile item updated successfully!');
      } else {
        // Create new item
        await api.post('/admin/mercantile/items', {
          name: mercantileForm.name,
          description: mercantileForm.description,
          price: parseInt(mercantileForm.price),
          category: mercantileForm.category,
          subcategory: mercantileForm.subcategory || null,
          image_url: mercantileForm.image_url || null,
          item_type: mercantileForm.item_type || 'inventory',
          inventory_key: mercantileForm.inventory_key || null,
          ranch_asset_key: mercantileForm.ranch_asset_key || null,
          stock: mercantileForm.stock ? parseInt(mercantileForm.stock) : -1,
          active: true
        });
        toast.success('Mercantile item created successfully!');
      }
      setShowMercantileDialog(false);
      setEditingMercantileItem(null);
      setMercantileForm({
        name: '',
        description: '',
        price: 0,
        category: '',
        subcategory: '',
        image_url: '',
        item_type: 'inventory',
        inventory_key: '',
        ranch_asset_key: '',
        stock: null
      });
      fetchMercantileItems();
    } catch (error) {
      console.error('Error saving mercantile item:', error);
      toast.error(editingMercantileItem ? 'Failed to update mercantile item' : 'Failed to create mercantile item');
    }
  };

  const handleEditMercantileItem = (item) => {
    setEditingMercantileItem(item);
    setMercantileForm({
      name: item.name || '',
      description: item.description || '',
      price: item.price || 0,
      category: item.category || '',
      subcategory: item.subcategory || '',
      image_url: item.image_url || '',
      item_type: item.item_type || 'inventory',
      inventory_key: item.inventory_key || '',
      ranch_asset_key: item.ranch_asset_key || '',
      stock: item.stock === -1 ? null : item.stock
    });
    setShowMercantileDialog(true);
  };

  const handleUpdateMercantileItem = async (itemId, updates) => {
    try {
      await api.put(`/admin/mercantile/items/${itemId}`, updates);
      toast.success('Item updated successfully!');
      fetchMercantileItems();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    }
  };

  const handleDeleteMercantileItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await api.delete(`/admin/mercantile/items/${itemId}`);
      toast.success('Item deleted successfully!');
      fetchMercantileItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleSeedMercantile = async () => {
    if (!window.confirm('This will seed the mercantile with default items. Continue?')) return;

    try {
      const response = await api.post('/admin/mercantile/seed');
      toast.success(response.data.message);
      fetchMercantileItems();
    } catch (error) {
      console.error('Error seeding mercantile:', error);
      toast.error('Failed to seed mercantile');
    }
  };

  // ============= COIN ACTIVITIES FUNCTIONS =============
  
  const fetchCoinActivities = async () => {
    try {
      const response = await api.get('/admin/coin-activities');
      setCoinActivities(response.data);
    } catch (error) {
      console.error('Error fetching coin activities:', error);
    }
  };

  const handleCreateCoinActivity = async () => {
    try {
      await api.post('/admin/coin-activities', {
        activity_name: coinActivityForm.activity_name,
        activity_type: coinActivityForm.activity_type,
        coins_reward: parseInt(coinActivityForm.coins_reward),
        description: coinActivityForm.description,
        max_per_day: coinActivityForm.max_per_day ? parseInt(coinActivityForm.max_per_day) : null
      });
      toast.success('Coin activity created successfully!');
      setCoinActivityForm({
        activity_name: '',
        activity_type: '',
        coins_reward: 0,
        description: '',
        max_per_day: null
      });
      fetchCoinActivities();
    } catch (error) {
      console.error('Error creating coin activity:', error);
      toast.error('Failed to create coin activity');
    }
  };

  // ============= DATABASE BACKUP MANAGEMENT =============

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await api.get('/admin/list-backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to load backups');
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const response = await api.post('/admin/backup-database');
      toast.success('Database backup created successfully!');
      fetchBackups(); // Refresh backup list
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (backupFilename) => {
    if (!window.confirm(`⚠️ WARNING: This will restore the database to the selected backup.\n\nThis will OVERWRITE all current data!\n\nBackup: ${backupFilename}\n\nAre you sure you want to continue?`)) {
      return;
    }

    try {
      const response = await api.post('/admin/restore-database', {
        backup_filename: backupFilename
      });
      toast.success('Database restored successfully! Please refresh the page.');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    }
  };

  const handleDeleteCoinActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await api.delete(`/admin/coin-activities/${activityId}`);
      toast.success('Activity deleted successfully!');
      fetchCoinActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Failed to delete activity');
    }
  };


  const openEditQuestion = (module, question) => {
    setSelectedModule(module);
    setEditingQuestion(question);
    
    // Convert current module title to section format to show in dropdown
    const currentModuleSection = module.title.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    // Normalize options to new object format (handle both old string format and new object format)
    const normalizedOptions = (question.options || []).map(opt => {
      if (typeof opt === 'string') {
        return { text: opt, photo_url: '' };
      }
      return { text: opt.text || '', photo_url: opt.photo_url || '' };
    });
    
    // Ensure at least 2 options
    while (normalizedOptions.length < 2) {
      normalizedOptions.push({ text: '', photo_url: '' });
    }
    
    // Normalize correct to array format (handle both old single int and new array format)
    // Filter out any null/undefined values
    const normalizedCorrect = (Array.isArray(question.correct) 
      ? question.correct 
      : [question.correct]).filter(v => v !== null && v !== undefined);
    
    // Ensure at least one correct answer
    const safeCorrect = normalizedCorrect.length > 0 ? normalizedCorrect : [0];
    
    setQuestionForm({
      question: question.question,
      options: normalizedOptions,
      correct: safeCorrect,
      explanation: question.explanation,
      order: question.order,
      section: currentModuleSection, // Set to current module, not default
      photo_url: question.photo_url || ''
    });
    setShowQuestionDialog(true);
  };

  // Helper function to find module ID from section name
  const getModuleIdFromSection = (sectionValue) => {
    const module = modules.find(m => 
      m.title.toLowerCase().replace(/[^a-z0-9]/g, '_') === sectionValue
    );
    return module ? module.id : null;
  };

  const openAddQuestion = (module) => {
    setSelectedModule(module);
    setEditingQuestion(null);
    setQuestionForm({
      question: '',
      options: [
        { text: '', photo_url: '' },
        { text: '', photo_url: '' }
      ],
      correct: [0],
      explanation: '',
      order: module.questions ? module.questions.length : 0,
      photo_url: ''
    });
    setShowQuestionDialog(true);
  };

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#FCFCF8'}}>
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4" style={{color: '#D66238'}} />
          <h2 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Admin Access Required</h2>
          <p style={{color: '#D66238', opacity: 0.7}}>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold mb-8" style={{color: '#D66238'}}>
          Admin Panel
        </h1>

        <Tabs value={activeAdminTab} onValueChange={setActiveAdminTab} className="w-full">
          <TabsList className="mb-8 flex-wrap gap-2 h-auto justify-start overflow-x-auto">
            <TabsTrigger value="dashboard">
              <Shield className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="modules">
              <BookOpen className="w-4 h-4 mr-2" />
              Quiz Modules
            </TabsTrigger>
            <TabsTrigger value="resources">
              <ExternalLink className="w-4 h-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="trainers">
              <MessageCircle className="w-4 h-4 mr-2" />
              Trainers
            </TabsTrigger>
            <TabsTrigger value="pricing">
              <CreditCard className="w-4 h-4 mr-2" />
              Pricing Plans
            </TabsTrigger>
            <TabsTrigger value="adoption-guide">
              <BookOpen className="w-4 h-4 mr-2" />
              Adoption Guide
            </TabsTrigger>
            <TabsTrigger value="virtual-corral">
              <Award className="w-4 h-4 mr-2" />
              Virtual Corral
            </TabsTrigger>
            <TabsTrigger value="virtual-mustangs">
              <Heart className="w-4 h-4 mr-2" />
              Virtual Mustangs
            </TabsTrigger>
            <TabsTrigger value="photo-gallery">
              <Image className="w-4 h-4 mr-2" />
              Photo Gallery
            </TabsTrigger>
            <TabsTrigger value="ranch-game">
              <Home className="w-4 h-4 mr-2" />
              Ranch Game
            </TabsTrigger>
            <TabsTrigger value="coupons">
              <Award className="w-4 h-4 mr-2" />
              Coupon Codes
            </TabsTrigger>
            <TabsTrigger value="mercantile">
              <DollarSign className="w-4 h-4 mr-2" />
              Mercantile
            </TabsTrigger>
            <TabsTrigger value="coin-activities">
              <Award className="w-4 h-4 mr-2" />
              Coin Activities
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="community">
              <Users className="w-4 h-4 mr-2" />
              Community
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Upload className="w-4 h-4 mr-2" />
              App Settings
            </TabsTrigger>
            <TabsTrigger value="backups">
              <Save className="w-4 h-4 mr-2" />
              DB Backups
            </TabsTrigger>
            <TabsTrigger value="media-library">
              <Package className="w-4 h-4 mr-2" />
              Media Library
            </TabsTrigger>
            <TabsTrigger value="ranch-blocks">
              <Home className="w-4 h-4 mr-2" />
              Ranch Blocks
            </TabsTrigger>
            <TabsTrigger value="ranchette">
              <Home className="w-4 h-4 mr-2" />
              Ranchette Preview
            </TabsTrigger>
            <TabsTrigger value="herd-settings">
              <Heart className="w-4 h-4 mr-2" />
              Herd Settings
            </TabsTrigger>
            <TabsTrigger value="data-migration">
              <Database className="w-4 h-4 mr-2" />
              Data Migration
            </TabsTrigger>
            <TabsTrigger value="coin-manager">
              <DollarSign className="w-4 h-4 mr-2" />
              Coin Manager
            </TabsTrigger>
            <TabsTrigger value="subscriptions" onClick={fetchSubscriptions}>
              <CreditCard className="w-4 h-4 mr-2" />
              Subscriptions
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Dashboard Module Management</h3>
              <p className="text-gray-600">Customize the icons and appearance of dashboard modules. Changes will appear on the main dashboard.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboardModules.map((module) => (
                <div
                  key={module.id}
                  className="rounded-2xl overflow-hidden shadow-lg"
                  style={{backgroundColor: module.bg_color, border: '2px solid #fff'}}
                >
                  <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {module.icon_image_url ? (
                          <div className="w-12 h-12 rounded-full bg-white bg-opacity-30 flex items-center justify-center p-2">
                            <img src={module.icon_image_url} alt={module.title} className="w-full h-full object-contain" />
                          </div>
                        ) : module.icon_name && (() => {
                          const IconComponent = availableIcons.find(i => i.name === module.icon_name)?.component;
                          return IconComponent ? (
                            <div className="w-12 h-12 rounded-full bg-white bg-opacity-30 flex items-center justify-center">
                              <IconComponent className="w-6 h-6" />
                            </div>
                          ) : null;
                        })()}
                        <div>
                          <h3 className="text-xl font-bold">{module.title}</h3>
                          <p className="text-xs opacity-90">{module.module_key}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm mb-4 opacity-90">{module.description}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openEditDashModule(module)}
                        size="sm"
                        style={{backgroundColor: 'white', color: module.bg_color}}
                        className="hover:opacity-90"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Customize
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* QUIZ MODULES TAB */}
          <TabsContent value="modules">
            <div className="mb-6 flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  setEditingModule(null);
                  setModuleForm({ title: '', description: '', order: modules.length });
                  setShowModuleDialog(true);
                }}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Module
              </Button>
              <Button
                onClick={handleSeedAllModules}
                disabled={seedingModules}
                variant="outline"
                style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
              >
                {seedingModules ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Seed Quiz Modules
                  </>
                )}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info('Backing up quiz modules to server...');
                    const response = await api.post('/admin/backup-quiz-modules');
                    toast.success(`Backup saved! ${response.data.total_modules} modules, ${response.data.total_questions} questions`);
                  } catch (error) {
                    toast.error('Failed to backup: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                variant="default"
                style={{backgroundColor: '#22c55e', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                Backup to Server
              </Button>
              <Button
                onClick={handleSeedAdoptionQuestions}
                disabled={seedingAdoption}
                variant="outline"
                style={{borderColor: '#D66238', color: '#D66238'}}
              >
                {seedingAdoption ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Seed Adoption Questions (BLM)
                  </>
                )}
              </Button>
              <Button
                onClick={handleSeedTransportQuestions}
                disabled={seedingTransport}
                variant="outline"
                style={{borderColor: '#D66238', color: '#D66238'}}
              >
                {seedingTransport ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Seed Transport Questions
                  </>
                )}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    toast.info('Exporting all quiz modules...');
                    const response = await api.get('/admin/export-quiz-modules');
                    const data = response.data;
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `quiz-modules-backup-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`Exported ${data.total_modules} modules with ${data.total_questions} questions!`);
                  } catch (error) {
                    toast.error('Failed to export quiz modules');
                  }
                }}
                variant="outline"
                style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
              >
                <Download className="w-4 h-4 mr-2" />
                Export All Quizzes
              </Button>
              <Button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.json';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      toast.info('Importing quiz modules...');
                      const text = await file.text();
                      const data = JSON.parse(text);
                      const response = await api.post('/admin/import-quiz-modules', data);
                      toast.success(`Imported ${response.data.imported} new, updated ${response.data.updated} existing modules!`);
                      fetchModules();
                    } catch (error) {
                      toast.error('Failed to import: ' + (error.message || 'Invalid file'));
                    }
                  };
                  input.click();
                }}
                variant="outline"
                style={{borderColor: '#D66238', color: '#D66238'}}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Quizzes
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12" style={{color: '#D66238'}}>
                Loading modules...
              </div>
            ) : (
              <Accordion type="single" collapsible className="space-y-3">
                {modules.map((module) => (
                  <AccordionItem
                    key={module.id}
                    value={module.id}
                    className="rounded-2xl overflow-hidden"
                    style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="text-left">
                          <h3 className="text-xl font-bold" style={{color: '#D66238'}}>
                            {module.title}
                          </h3>
                          <p className="text-xs mt-1" style={{color: '#6FCFD3'}}>
                            {module.questions?.length || 0} questions • Order: {module.order}
                          </p>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModule(module);
                            }}
                            size="sm"
                            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteModule(module.id);
                            }}
                            size="sm"
                            style={{backgroundColor: '#D66238', color: '#fff'}}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <p className="text-sm mb-4" style={{color: '#D66238', opacity: 0.7}}>
                        {module.description}
                      </p>

                      {/* Questions List */}
                      <div className="border-t pt-4" style={{borderColor: '#6FCFD3'}}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold" style={{color: '#D66238'}}>Questions</h4>
                          <Button
                            onClick={() => openAddQuestion(module)}
                            size="sm"
                            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Question
                          </Button>
                        </div>
                        
                        {module.questions && module.questions.length > 0 ? (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {module.questions.map((question, idx) => (
                              <div
                                key={question.id}
                                className="p-4 rounded-xl"
                                style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                                      Q{idx + 1}: {question.question}
                                    </p>
                                    <p className="text-xs" style={{color: '#6FCFD3'}}>
                                      Correct: {
                                        (() => {
                                          const correctIndices = Array.isArray(question.correct) 
                                            ? question.correct 
                                            : [question.correct];
                                          return correctIndices.map(ci => {
                                            const opt = question.options[ci];
                                            if (!opt) return '?';
                                            return typeof opt === 'string' ? opt : (opt.text || '[Photo]');
                                          }).join(', ');
                                        })()
                                      }
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => openEditQuestion(module, question)}
                                      className="px-2 py-1 rounded hover:bg-gray-100"
                                      type="button"
                                      style={{color: '#6FCFD3'}}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDuplicateQuestion(module.id, question)}
                                      className="px-2 py-1 rounded hover:bg-gray-100"
                                      type="button"
                                      title="Duplicate Question"
                                    >
                                      <Copy className="w-4 h-4" style={{color: '#10B981'}} />
                                    </button>
                                    <button
                                      onClick={() => {
                                        fetch(`${window.location.origin}/api/admin/quiz-modules/${module.id}/questions/${question.id}`, {
                                          method: 'DELETE',
                                          credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' }
                                        }).then(response => {
                                          if (response.ok) fetchModules();
                                          else alert('Delete failed');
                                        }).catch(err => alert('Error: ' + err.message));
                                      }}
                                      className="px-2 py-1 rounded hover:bg-red-50"
                                      type="button"
                                      style={{color: '#D66238'}}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-center py-4" style={{color: '#D66238', opacity: 0.5}}>
                            No questions yet. Click "Add Question" to get started.
                          </p>
                        )}
                      </div>

                      {/* Study Guide / Outline Section */}
                      <div className="mt-4 border-t pt-4" style={{borderColor: '#6FCFD3'}}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold" style={{color: '#D66238'}}>Study Guide (Printable)</h4>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleGenerateOutline(module.id)}
                              size="sm"
                              variant="outline"
                              title="Auto-generate from questions"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Auto-Generate
                            </Button>
                            <Button
                              onClick={() => openEditOutline(module)}
                              size="sm"
                              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Study Guide
                            </Button>
                          </div>
                        </div>
                        
                        {module.outline_content && module.outline_content.length > 0 ? (
                          <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}>
                            <p className="text-xs mb-2" style={{color: '#6FCFD3'}}>
                              {module.outline_content.length} items • Preview:
                            </p>
                            <div className="text-sm space-y-1 max-h-40 overflow-y-auto" style={{color: '#D66238'}}>
                              {module.outline_content.slice(0, 5).map((item, idx) => (
                                <p key={idx} className="truncate">{item || '(empty line)'}</p>
                              ))}
                              {module.outline_content.length > 5 && (
                                <p className="text-xs italic" style={{color: '#6FCFD3'}}>
                                  ... and {module.outline_content.length - 5} more items
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-center py-4" style={{color: '#D66238', opacity: 0.5}}>
                            No study guide yet. Click "Auto-Generate" or "Edit Study Guide".
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </TabsContent>

          {/* RESOURCES TAB */}
          <TabsContent value="resources">
            <div className="mb-6">
              <Button
                onClick={() => {
                  setEditingResource(null);
                  setResourceForm({ title: '', description: '', link: '', category: 'general', order: resources.length });
                  setShowResourceDialog(true);
                }}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Resource
              </Button>
            </div>

            <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
              <h3 className="text-2xl font-bold mb-6" style={{color: '#D66238'}}>Manage Resources</h3>
              
              {resources.length === 0 ? (
                <div className="text-center py-12" style={{color: '#D66238', opacity: 0.7}}>
                  <ExternalLink className="w-16 h-16 mx-auto mb-4" style={{opacity: 0.5}} />
                  <p>No resources yet. Click "Add New Resource" to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {resources.map((resource) => (
                    <div
                      key={resource.id}
                      className="flex items-center justify-between p-4 rounded-xl"
                      style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1" style={{color: '#D66238'}}>
                          {resource.title}
                        </h4>
                        <p className="text-sm mb-2" style={{color: '#D66238', opacity: 0.7}}>
                          {resource.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs" style={{color: '#6FCFD3'}}>
                          <a href={resource.link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {resource.link}
                          </a>
                          <span>•</span>
                          <span>Category: {resource.category}</span>
                          <span>•</span>
                          <span>Order: {resource.order}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openEditResource(resource)}
                          size="sm"
                          style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteResource(resource.id)}
                          size="sm"
                          style={{backgroundColor: '#D66238', color: '#fff'}}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* USER MANAGEMENT TAB */}
          {/* TRAINERS TAB */}
          <TabsContent value="trainers">
            <div className="mb-6 flex gap-3 flex-wrap">
              <Button
                onClick={() => {
                  setEditingTrainer(null);
                  setTrainerForm({ name: '', photo_url: '', bio: '', city: '', state: '', specialties: [], availability: 'Available', response_time: 'Within 24 hours' });
                  setShowTrainerDialog(true);
                }}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Trainer
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const response = await api.get('/admin/export-trainers');
                    const data = response.data;
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `trainers-backup-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`Exported ${data.trainers.length} trainers with ${data.media_files.length} images`);
                  } catch (error) {
                    toast.error('Failed to export trainers');
                  }
                }}
                variant="outline"
                style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Trainers
              </Button>
              <Button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.json';
                  input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const text = await file.text();
                      const data = JSON.parse(text);
                      const response = await api.post('/admin/import-trainers', data);
                      toast.success(`Imported ${response.data.imported_trainers} trainers, ${response.data.imported_media} images`);
                      fetchTrainers();
                    } catch (error) {
                      toast.error('Failed to import: ' + (error.message || 'Invalid file'));
                    }
                  };
                  input.click();
                }}
                variant="outline"
                style={{borderColor: '#D66238', color: '#D66238'}}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Trainers
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trainers.map((trainer) => (
                <div
                  key={trainer.id}
                  className="p-6 rounded-2xl"
                  style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                >
                  <div className="flex items-start gap-4 mb-4">
                    {trainer.photo_url ? (
                      <img src={getImageUrl(trainer.photo_url)} alt={trainer.name} className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{backgroundColor: '#6FCFD3'}}>
                        <span className="text-white text-2xl font-bold">{trainer.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-1" style={{color: '#D66238'}}>{trainer.name}</h3>
                      <p className="text-sm mb-2" style={{color: '#372F2F', opacity: 0.7}}>{trainer.availability}</p>
                      {!trainer.is_active && (
                        <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Inactive</span>
                      )}
                    </div>
                  </div>

                  <p className="text-sm mb-4" style={{color: '#372F2F'}}>{trainer.bio}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {trainer.specialties?.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full"
                        style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditTrainer(trainer)}
                      size="sm"
                      variant="outline"
                      style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={async () => {
                        await api.delete(`/admin/trainers/${trainer.id}`);
                        toast.success('Trainer deleted!');
                        fetchTrainers();
                      }}
                      size="sm"
                      variant="outline"
                      style={{borderColor: '#D66238', color: '#D66238'}}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PRICING PLANS TAB */}
          <TabsContent value="pricing">
            <div className="mb-6">
              <Button
                onClick={() => {
                  setEditingPlan(null);
                  setPlanForm({ plan_type: '', name: '', description: '', price: 0, duration_days: null });
                  setShowPlanDialog(true);
                }}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Plan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="p-6 rounded-2xl"
                  style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                >
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>{plan.name}</h3>
                    <div className="text-4xl font-bold mb-2" style={{color: '#6FCFD3'}}>
                      ${plan.price}
                    </div>
                    {plan.duration_days && (
                      <p className="text-sm text-gray-600">for {plan.duration_days} days</p>
                    )}
                  </div>

                  <p className="text-sm mb-4 text-center" style={{color: '#372F2F'}}>{plan.description}</p>

                  <div className="mb-4 p-2 rounded" style={{backgroundColor: '#f0fffe'}}>
                    <p className="text-xs font-semibold mb-1" style={{color: '#D66238'}}>Plan Type:</p>
                    <p className="text-sm" style={{color: '#372F2F'}}>{plan.plan_type}</p>
                  </div>

                  {!plan.is_active && (
                    <div className="mb-4 text-center">
                      <span className="text-xs px-3 py-1 rounded bg-red-100 text-red-700">Inactive</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditPlan(plan)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeletePlan(plan.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      style={{borderColor: '#D66238', color: '#D66238'}}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ADOPTION GUIDE TAB */}
          <TabsContent value="adoption-guide">
            <div className="mb-6">
              <Button
                onClick={() => {
                  setEditingSection(null);
                  setSectionForm({ title: '', content: '', order: adoptionSections.length + 1, icon: '' });
                  setShowSectionDialog(true);
                }}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Section
              </Button>
            </div>

            <div className="space-y-4">
              {adoptionSections.map((section) => (
                <div
                  key={section.id}
                  className="p-6 rounded-2xl"
                  style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#6FCFD3'}}>
                          <span className="text-white font-bold">{section.order}</span>
                        </div>
                        {/* Show custom icon image if available, otherwise show pre-made icon */}
                        {section.icon_image_url ? (
                          <div className="w-12 h-12 rounded flex items-center justify-center p-1" style={{backgroundColor: '#f0fffe', border: '2px solid #6FCFD3'}}>
                            <img src={section.icon_image_url} alt="Custom icon" className="w-full h-full object-contain" />
                          </div>
                        ) : section.icon && (() => {
                          const IconComponent = availableIcons.find(i => i.name === section.icon)?.component;
                          return IconComponent ? (
                            <div className="w-8 h-8 rounded flex items-center justify-center" style={{backgroundColor: '#f0fffe'}}>
                              <IconComponent className="w-5 h-5" style={{color: '#6FCFD3'}} />
                            </div>
                          ) : null;
                        })()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2" style={{color: '#D66238'}}>{section.title}</h3>
                        {section.icon_image_url ? (
                          <p className="text-xs text-gray-500 mb-2">Icon: Custom Image</p>
                        ) : section.icon && (
                          <p className="text-xs text-gray-500 mb-2">
                            Icon: {availableIcons.find(i => i.name === section.icon)?.label || section.icon}
                          </p>
                        )}
                        <div 
                          className="text-sm text-gray-700 prose prose-sm max-h-32 overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      </div>
                    </div>
                    {!section.is_active && (
                      <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 ml-2">Inactive</span>
                    )}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => openEditSection(section)}
                      size="sm"
                      variant="outline"
                      style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteSection(section.id)}
                      size="sm"
                      variant="outline"
                      style={{borderColor: '#D66238', color: '#D66238'}}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* VIRTUAL CORRAL TAB */}
          <TabsContent value="virtual-corral">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #D66238'}}>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Virtual Corral Settings</h3>
              <p className="text-gray-600">Manage subscription pricing and game limits for the Virtual Corral module.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Subscription Pricing */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #D66238'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Monthly Subscription</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Monthly Price (USD)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={vcSettingsForm.monthly_price}
                        onChange={(e) => setVcSettingsForm({ ...vcSettingsForm, monthly_price: parseFloat(e.target.value) || 0 })}
                        className="text-2xl font-bold"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Current: ${virtualCorralSettings?.monthly_price || 9.99}/month
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600 mb-2">What's Included:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>Access to Virtual Corral gaming module</li>
                      <li>Ranch management features</li>
                      <li>Facility design tools</li>
                      <li>Virtual mustang adoption & care</li>
                      <li>Progress tracking & achievements</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Game Limits */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #D66238'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Game Limits</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Max Mustangs Per Event
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={vcSettingsForm.max_mustangs_per_event}
                      onChange={(e) => setVcSettingsForm({ ...vcSettingsForm, max_mustangs_per_event: parseInt(e.target.value) || 4 })}
                      className="text-xl"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Users can adopt up to this many mustangs from a single adoption event (OLC, Satellite, or Holding Facility)
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Current: {virtualCorralSettings?.max_mustangs_per_event || 4} mustangs
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm font-semibold mb-2" style={{color: '#D66238'}}>Recommended:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>1-4 mustangs: Beginner friendly</li>
                      <li>5-6 mustangs: Intermediate challenge</li>
                      <li>7-10 mustangs: Advanced players</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleUpdateVcSettings}
                disabled={isSubmitting}
                className="px-8 py-3"
                style={{backgroundColor: '#D66238', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </TabsContent>

          {/* VIRTUAL MUSTANGS TAB */}
          <TabsContent value="virtual-mustangs">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Virtual Mustang Management</h3>
                  <p className="text-gray-600">Add and manage virtual mustangs available for adoption. Total: {virtualMustangs.length}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={async () => {
                      try {
                        toast.info('Backing up virtual mustangs to server...');
                        const response = await api.post('/admin/backup-virtual-mustangs');
                        toast.success(`Server backup saved! ${response.data.total_mustangs} mustangs backed up.`);
                      } catch (error) {
                        toast.error('Failed to backup: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    variant="default"
                    style={{backgroundColor: '#22c55e', color: '#fff'}}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save to Server
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const response = await api.get('/admin/export-virtual-corral');
                        const data = response.data;
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `virtual-corral-backup-${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success(`Exported ${data.total_mustangs} mustangs`);
                      } catch (error) {
                        toast.error('Failed to export virtual corral data');
                      }
                    }}
                    variant="outline"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Backup
                  </Button>
                  <Button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.json';
                      input.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        try {
                          const text = await file.text();
                          const data = JSON.parse(text);
                          const response = await api.post('/admin/import-virtual-corral', data);
                          toast.success(`Imported ${response.data.imported_mustangs} mustangs, ${response.data.imported_media} images`);
                          fetchVirtualMustangs();
                        } catch (error) {
                          toast.error('Failed to import: ' + (error.message || 'Invalid file'));
                        }
                      };
                      input.click();
                    }}
                    variant="outline"
                    style={{borderColor: '#D66238', color: '#D66238'}}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import Backup
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const response = await api.post('/admin/virtual-mustangs/migrate-rotation-groups');
                        toast.success(`Rotation groups assigned! ${response.data.migrated} mustangs updated. Distribution: G0=${response.data.distribution.group_0}, G1=${response.data.distribution.group_1}, G2=${response.data.distribution.group_2}`);
                        fetchVirtualMustangs();
                      } catch (error) {
                        toast.error('Failed to migrate rotation groups: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    variant="outline"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Setup Rotation
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingVirtualMustang(null);
                      setVirtualMustangForm({
                        name: '',
                        age: 0,
                        gender: '',
                        color: '',
                        hma: '',
                        height: '',
                        disposition: '',
                        photo_url: '',
                        photo_urls: [],
                        adoption_method: 'olc'
                      });
                      setShowVirtualMustangDialog(true);
                    }}
                    style={{backgroundColor: '#D66238', color: 'white'}}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Virtual Mustang
                  </Button>
                </div>
              </div>
            </div>

            {/* Mustangs Without Photos - Quick Upload Section */}
            {virtualMustangs.filter(m => !m.photo_url).length > 0 && (
              <div className="mb-6 p-4 rounded-lg border-2 border-dashed" style={{borderColor: '#EF4444', backgroundColor: '#fef2f2'}}>
                <h4 className="font-bold mb-3 flex items-center gap-2" style={{color: '#EF4444'}}>
                  ⚠️ {virtualMustangs.filter(m => !m.photo_url).length} Mustangs Need Photos
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Tap on a mustang below to quickly upload a photo. You can select multiple photos at once.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {virtualMustangs.filter(m => !m.photo_url).map((mustang) => (
                    <label
                      key={mustang.id}
                      className="relative flex flex-col items-center p-3 rounded-lg cursor-pointer hover:bg-white transition border-2"
                      style={{borderColor: quickUploadMustangId === mustang.id ? '#6FCFD3' : '#ddd', backgroundColor: quickUploadMustangId === mustang.id ? '#f0f9fa' : 'white'}}
                    >
                      <input
                        type="file"
                        accept="image/*,.heic,.heif"
                        multiple
                        className="hidden"
                        onChange={(e) => handleQuickPhotoUpload(e, mustang.id)}
                        disabled={quickUploadMustangId !== null}
                      />
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-2" style={{backgroundColor: '#6FCFD3'}}>
                        {quickUploadMustangId === mustang.id ? '⏳' : '📷'}
                      </div>
                      <span className="text-xs font-medium text-center truncate w-full" style={{color: '#372F2F'}}>
                        {mustang.name}
                      </span>
                      {quickUploadMustangId === mustang.id && (
                        <span className="text-xs text-blue-500 mt-1">Uploading...</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Rotation Info Banner */}
            <div className="mb-6 p-4 rounded-lg border" style={{backgroundColor: '#f0f9fa', borderColor: '#6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold" style={{color: '#D66238'}}>11-Day Rotation System</h4>
                  <p className="text-sm text-gray-600">
                    Mustangs rotate between adoption methods every 11 days. Each mustang appears in only ONE method at a time.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Rotation Groups</div>
                  <div className="flex gap-2 mt-1">
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{backgroundColor: '#D66238', color: 'white'}}>OLC</span>
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{backgroundColor: '#6FCFD3', color: 'white'}}>Satellite</span>
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{backgroundColor: '#372F2F', color: 'white'}}>Holding</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mustangs by Adoption Method */}
            <div className="space-y-8">
              {['olc', 'satellite', 'holding'].map((method) => {
                const methodMustangs = virtualMustangs.filter(m => m.adoption_method === method);
                const methodNames = {
                  olc: 'Online Corral (OLC)',
                  satellite: 'Satellite Events',
                  holding: 'Holding Facilities'
                };
                
                return (
                  <div key={method}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xl font-bold flex items-center gap-3" style={{color: '#D66238'}}>
                        <span className="px-4 py-2 rounded-lg" style={{backgroundColor: '#6FCFD3', color: 'white'}}>
                          {methodNames[method]}
                        </span>
                        <span className="text-sm text-gray-600">({methodMustangs.length} mustangs)</span>
                      </h4>
                      <Button
                        onClick={() => {
                          setEditingVirtualMustang(null);
                          setVirtualMustangForm({
                            name: '',
                            age: 0,
                            gender: '',
                            color: '',
                            hma: '',
                            height: '',
                            disposition: '',
                            photo_url: '',
                            photo_urls: [],
                            adoption_method: method
                          });
                          setShowVirtualMustangDialog(true);
                        }}
                        size="sm"
                        style={{backgroundColor: '#D66238', color: 'white'}}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add to {methodNames[method]}
                      </Button>
                    </div>
                    
                    {methodMustangs.length === 0 ? (
                      <div className="text-center py-8 bg-white rounded-xl shadow" style={{border: '2px dashed #e5e7eb'}}>
                        <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" style={{color: '#D66238'}} />
                        <p className="text-gray-500">No mustangs in {methodNames[method]}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {methodMustangs.map((mustang) => (
                          <div
                            key={mustang.id}
                            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition"
                            style={{border: '2px solid #6FCFD3'}}
                          >
                            {/* Mustang Photo - Vertical Rectangle */}
                            <div className="relative aspect-[3/4] overflow-hidden" style={{backgroundColor: '#6FCFD3'}}>
                              {mustang.photo_url ? (
                                <img 
                                  src={getImageUrl(mustang.photo_url)}
                                  alt={mustang.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                                  <span className="text-5xl mb-2">📷</span>
                                  <span className="text-white text-sm font-medium">No Photo</span>
                                </div>
                              )}
                              {/* Quick Upload Button */}
                              <label 
                                className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-bold bg-white/90 text-gray-800 cursor-pointer hover:bg-white transition flex items-center gap-1"
                                title="Upload photo"
                              >
                                <input
                                  type="file"
                                  accept="image/*,.heic,.heif"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handleQuickPhotoUpload(e, mustang.id)}
                                  disabled={quickUploadMustangId === mustang.id}
                                />
                                {quickUploadMustangId === mustang.id ? '⏳' : '📤'} 
                                {quickUploadMustangId === mustang.id ? 'Uploading...' : 'Add Photo'}
                              </label>
                              {/* Rotation Group Badge */}
                              {mustang.rotation_group !== undefined && (
                                <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-black/50 text-white">
                                  G{mustang.rotation_group}
                                </div>
                              )}
                              {/* Photo Count Badge */}
                              {mustang.photo_urls && mustang.photo_urls.length > 1 && (
                                <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold bg-black/50 text-white">
                                  📷 {mustang.photo_urls.length}
                                </div>
                              )}
                            </div>

                            {/* Mustang Info Card */}
                            <div className="p-3">
                              <h3 className="text-base font-bold mb-2 truncate" style={{color: '#D66238'}}>
                                {mustang.name}
                              </h3>
                              
                              <div className="text-xs text-gray-600 mb-3 space-y-1">
                                <div className="flex justify-between">
                                  <span>{mustang.age}yo {mustang.gender}</span>
                                  <span>{mustang.color}</span>
                                </div>
                                <div className="truncate">{mustang.hma}</div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => openEditVirtualMustang(mustang)}
                                  size="sm"
                                  className="flex-1 h-8 text-xs"
                                  style={{backgroundColor: '#6FCFD3', color: 'white'}}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => handleDeleteVirtualMustang(mustang.id, mustang.name)}
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  style={{borderColor: '#ef4444', color: '#ef4444'}}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Photo Gallery Tab */}
          <TabsContent value="photo-gallery">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-2xl font-bold" style={{color: '#6FCFD3'}}>Photo Gallery</h3>
                  <p className="text-gray-600">Upload, view, and assign photos to mustangs</p>
                </div>
                <div className="flex gap-2">
                  {selectedPhotosForBulkAssign.length > 0 && (
                    <Button
                      onClick={handleBulkDeletePhotos}
                      disabled={deletingPhotos}
                      style={{backgroundColor: '#ef4444', color: '#fff'}}
                    >
                      {deletingPhotos ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-2" /> Delete {selectedPhotosForBulkAssign.length}</>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={loadPhotoGallery}
                    disabled={loadingPhotos}
                    variant="outline"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    {loadingPhotos ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Refresh</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Multi-Select Controls - Shows when photos exist */}
              {unusedPhotos.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-800">
                      {selectedPhotosForBulkAssign.length > 0 
                        ? `${selectedPhotosForBulkAssign.length} of ${unusedPhotos.length} photos selected`
                        : `Select photos to bulk assign`
                      }
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={selectAllPhotos}
                        disabled={selectedPhotosForBulkAssign.length === unusedPhotos.length}
                        className="text-xs"
                      >
                        Select All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={deselectAllPhotos}
                        disabled={selectedPhotosForBulkAssign.length === 0}
                        className="text-xs"
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  {selectedPhotosForBulkAssign.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => setShowBulkAssignModal(true)}
                        style={{backgroundColor: '#22c55e', color: '#fff'}}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Assign {selectedPhotosForBulkAssign.length} Photo{selectedPhotosForBulkAssign.length > 1 ? 's' : ''} to Mustang
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Bulk Assign Modal */}
              {showBulkAssignModal && (
                <Dialog open={showBulkAssignModal} onOpenChange={() => setShowBulkAssignModal(false)}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Assign {selectedPhotosForBulkAssign.length} Photo{selectedPhotosForBulkAssign.length > 1 ? 's' : ''} to Virtual Mustang</DialogTitle>
                      <DialogDescription>
                        Select a mustang to add these photos to their gallery. Photos will be added to existing photos.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Preview selected photos */}
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                        {selectedPhotosForBulkAssign.map((photo) => (
                          <div key={photo.filename} className="relative">
                            <img 
                              src={getThumbUrl(photo.url)}
                              alt={photo.filename}
                              className="w-16 h-16 object-cover rounded shadow"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Select Mustang */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <span>🐴</span> Choose a Virtual Mustang
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                          {virtualMustangs.map((m) => (
                            <Button
                              key={m.id}
                              variant="outline"
                              size="sm"
                              disabled={assigningPhoto}
                              onClick={() => handleBulkAssignToMustang(m.id, m.name)}
                              className="justify-start text-left h-auto py-2"
                            >
                              <div className="flex items-center gap-2 w-full">
                                {m.photo_url ? (
                                  <img 
                                    src={getThumbUrl(m.photo_url)}
                                    alt={m.name}
                                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                                    <Image className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">{m.name}</div>
                                  <div className="text-xs text-gray-500 truncate">{m.color} {m.gender} • {(m.photo_urls?.length || 0)} photos</div>
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                        {virtualMustangs.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">No virtual mustangs found. Create some first!</p>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Upload Section - Drag & Drop */}
              <div 
                className={`mb-6 p-8 rounded-lg border-2 border-dashed transition-all ${
                  isDraggingOver 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingOver(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingOver(false);
                  const files = e.dataTransfer.files;
                  handlePhotoUpload(files);
                }}
              >
                <div className="text-center">
                  {uploadingPhotos ? (
                    <>
                      <RefreshCw className="w-12 h-12 mx-auto mb-3 animate-spin" style={{color: '#6FCFD3'}} />
                      <p className="text-lg font-medium text-gray-700">Uploading photos...</p>
                    </>
                  ) : isDraggingOver ? (
                    <>
                      <Upload className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p className="text-lg font-medium text-green-600">Drop photos here!</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-lg font-medium text-gray-700 mb-1">Drag & drop photos here</p>
                      <p className="text-sm text-gray-500 mb-4">or click the button below to select files</p>
                      <input
                        type="file"
                        id="photo-gallery-upload"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          handlePhotoUpload(e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        onClick={() => document.getElementById('photo-gallery-upload').click()}
                        style={{backgroundColor: '#22c55e', color: '#fff'}}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Select Photos
                      </Button>
                      <p className="text-xs text-gray-400 mt-3">Supports JPG, PNG, WebP, GIF (max 8MB each)</p>
                    </>
                  )}
                </div>
              </div>

              {/* Mustangs Needing Photos */}
              {mustangsNeedingPhotos.length > 0 && (
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-bold text-yellow-800 mb-2">⚠️ Mustangs Needing Photo Review ({mustangsNeedingPhotos.length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {mustangsNeedingPhotos.map((m) => (
                      <div key={m.id} className="p-2 bg-white rounded border text-sm">
                        <div className="font-medium">{m.name}</div>
                        <div className="text-gray-500 text-xs">{m.gender}, {m.color}</div>
                        <div className="text-gray-500 text-xs">{m.hma}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Assignment Modal */}
              {selectedPhotoForAssign && (
                <Dialog open={!!selectedPhotoForAssign} onOpenChange={() => setSelectedPhotoForAssign(null)}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Assign Photo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="flex justify-center">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${selectedPhotoForAssign.url}`}
                          alt="Selected photo"
                          className="max-h-48 rounded-lg shadow"
                        />
                      </div>
                      <p className="text-sm text-gray-500 text-center">
                        {selectedPhotoForAssign.filename} ({selectedPhotoForAssign.size_kb} KB)
                      </p>
                      
                      {/* Assign to Virtual Mustang */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <span>🐴</span> Assign to Virtual Mustang
                        </h4>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {virtualMustangs.map((m) => (
                            <Button
                              key={m.id}
                              variant="outline"
                              size="sm"
                              disabled={assigningPhoto}
                              onClick={async () => {
                                setAssigningPhoto(true);
                                try {
                                  console.log('Assigning photo:', selectedPhotoForAssign.url, 'to mustang:', m.id);
                                  await api.post('/admin/assign-photo-to-mustang', {
                                    mustang_id: m.id,
                                    photo_url: selectedPhotoForAssign.url
                                  });
                                  toast.success(`Photo assigned to ${m.name}!`);
                                  setSelectedPhotoForAssign(null);
                                  // Refresh data
                                  fetchVirtualMustangs();
                                  const photosRes = await api.get('/admin/unused-photos');
                                  setUnusedPhotos(photosRes.data.unused_photos || []);
                                } catch (error) {
                                  console.error('Assign photo error:', error.response?.data || error);
                                  toast.error('Failed to assign photo: ' + (error.response?.data?.detail || error.message));
                                } finally {
                                  setAssigningPhoto(false);
                                }
                              }}
                              className="justify-start text-left"
                            >
                              <span className="truncate">{m.name} - {m.color} {m.gender}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Assign to Trainer */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <span>👤</span> Assign to Trainer
                        </h4>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {trainers.map((t) => (
                            <Button
                              key={t.id}
                              variant="outline"
                              size="sm"
                              disabled={assigningPhoto}
                              onClick={async () => {
                                setAssigningPhoto(true);
                                try {
                                  await api.post('/admin/assign-photo-to-trainer', {
                                    trainer_id: t.id,
                                    photo_url: selectedPhotoForAssign.url
                                  });
                                  toast.success(`Photo assigned to ${t.name}!`);
                                  setSelectedPhotoForAssign(null);
                                  // Refresh data
                                  fetchTrainers();
                                  const photosRes = await api.get('/admin/unused-photos');
                                  setUnusedPhotos(photosRes.data.unused_photos || []);
                                } catch (error) {
                                  toast.error('Failed to assign photo');
                                } finally {
                                  setAssigningPhoto(false);
                                }
                              }}
                              className="justify-start text-left"
                            >
                              <span className="truncate">{t.name}</span>
                            </Button>
                          ))}
                          {trainers.length === 0 && (
                            <p className="text-sm text-gray-500 col-span-2">No trainers found. Add trainers in the Trainers tab.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Unused Photos Grid */}
              {unusedPhotos.length > 0 ? (
                <div>
                  <h4 className="font-bold mb-3">📷 Unused Photos ({unusedPhotos.length} of {photoGalleryTotal})</h4>
                  <p className="text-sm text-gray-600 mb-4">Check photos to select, or click to assign individually</p>
                  <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
                    {unusedPhotos.map((photo) => {
                      const isSelected = selectedPhotosForBulkAssign.some(p => p.filename === photo.filename);
                      return (
                        <div 
                          key={photo.filename}
                          className={`relative group border-2 rounded overflow-hidden hover:shadow-lg transition-all ${
                            isSelected 
                              ? 'border-green-500 ring-2 ring-green-300' 
                              : 'hover:border-blue-500'
                          }`}
                        >
                          {/* Selection checkbox */}
                          <div 
                            className="absolute top-1 left-1 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePhotoSelection(photo);
                            }}
                          >
                            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-green-500 border-green-500' 
                                : 'bg-white/80 border-gray-400 hover:border-green-500'
                            }`}>
                              {isSelected && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete this photo?\n${photo.filename}`)) return;
                              
                              try {
                                await api.delete(`/admin/media-library/${photo.filename}`);
                                toast.success('Photo deleted');
                                loadPhotoGallery();
                              } catch (error) {
                                toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                            className="absolute top-1 right-1 z-10 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            title="Delete photo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          
                          {/* Photo (clickable for single assignment) - Uses thumbnail for faster loading */}
                          <div 
                            className="cursor-pointer"
                            onClick={() => setSelectedPhotoForAssign(photo)}
                          >
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${photo.thumb_url || photo.url}`}
                              alt={photo.filename}
                              loading="lazy"
                              decoding="async"
                              width="200"
                              height="128"
                              className={`w-full h-32 object-cover transition-opacity ${isSelected ? 'opacity-90' : ''}`}
                              style={{ backgroundColor: '#f0f0f0' }}
                              onError={(e) => {
                                // Fallback to full image if thumbnail fails
                                if (photo.thumb_url && e.target.src.includes('/thumb/')) {
                                  e.target.src = `${process.env.REACT_APP_BACKEND_URL}${photo.url}`;
                                } else {
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999">Error</text></svg>';
                                }
                              }}
                            />
                            <div className={`p-2 ${isSelected ? 'bg-green-50' : 'bg-gray-50'}`}>
                              <p className="text-xs text-gray-500 truncate">{photo.filename}</p>
                              <p className="text-xs text-gray-400">{photo.size_kb} KB</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Load More Button */}
                  {photoGalleryHasMore && (
                    <div className="mt-4 text-center">
                      <Button
                        onClick={() => loadPhotoGallery(true)}
                        disabled={loadingPhotos}
                        variant="outline"
                        className="px-8"
                      >
                        {loadingPhotos ? 'Loading...' : `Load More (${photoGalleryTotal - unusedPhotos.length} remaining)`}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No unused photos. Upload some or click "Refresh" to reload.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="ranch-game">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #D66238'}}>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Ranch Game Settings</h3>
              <p className="text-gray-600">Manage settings for the Ranch Game module, including game title, description, and coming soon messaging.</p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Game Basic Info */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #D66238'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Game Information</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Game Title
                    </label>
                    <Input
                      type="text"
                      value={ranchGameForm.game_title}
                      onChange={(e) => setRanchGameForm({ ...ranchGameForm, game_title: e.target.value })}
                      placeholder="My Ranch Game"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Game Description
                    </label>
                    <Textarea
                      value={ranchGameForm.game_description}
                      onChange={(e) => setRanchGameForm({ ...ranchGameForm, game_description: e.target.value })}
                      placeholder="Build and manage your virtual ranch with interactive gameplay"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Coming Soon Message
                    </label>
                    <Textarea
                      value={ranchGameForm.coming_soon_message}
                      onChange={(e) => setRanchGameForm({ ...ranchGameForm, coming_soon_message: e.target.value })}
                      placeholder="The Ranch Game is currently under development..."
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      This message appears in the dialog when users click on the Ranch Game button
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Expected Release Date (Optional)
                    </label>
                    <Input
                      type="text"
                      value={ranchGameForm.release_date}
                      onChange={(e) => setRanchGameForm({ ...ranchGameForm, release_date: e.target.value })}
                      placeholder="Summer 2024"
                    />
                  </div>
                </div>
              </div>

              {/* Feature Image */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #D66238'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Feature Image</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                      Image URL or Upload
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={ranchGameForm.feature_image_url}
                        onChange={(e) => setRanchGameForm({ ...ranchGameForm, feature_image_url: e.target.value })}
                        placeholder="https://example.com/image.png"
                      />
                      <Button
                        onClick={() => ranchImageInputRef.current?.click()}
                        disabled={uploadingRanchImage}
                        variant="outline"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingRanchImage ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        ref={ranchImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleRanchImageUpload}
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      This image will be displayed in the Coming Soon dialog
                    </p>
                  </div>

                  {ranchGameForm.feature_image_url && (
                    <div className="mt-4">
                      <p className="text-sm font-semibold mb-2" style={{color: '#D66238'}}>Preview:</p>
                      <img 
                        src={ranchGameForm.feature_image_url} 
                        alt="Feature preview"
                        className="w-32 h-32 object-contain rounded-lg border-2 border-gray-200"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Game Status */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #D66238'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Game Status</h4>
                
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ranchGameForm.is_enabled}
                      onChange={(e) => setRanchGameForm({ ...ranchGameForm, is_enabled: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <span className="text-sm font-semibold">Enable Ranch Game</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  When enabled, users can access the Ranch Game. When disabled, the "Coming Soon" message will be shown.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleUpdateRanchGameSettings}
                disabled={isSubmitting}
                className="px-8 py-3"
                style={{backgroundColor: '#D66238', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </TabsContent>


          <TabsContent value="coupons">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Coupon Codes</h3>
                  <p className="text-gray-600">Create and manage discount coupons for payments</p>
                </div>
                <Button
                  onClick={() => setShowCouponDialog(true)}
                  className="flex items-center gap-2"
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  <Plus size={20} />
                  Create Coupon
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full">
                <thead style={{backgroundColor: '#6FCFD3'}}>
                  <tr>
                    <th className="px-6 py-3 text-left text-white">Code</th>
                    <th className="px-6 py-3 text-left text-white">Discount</th>
                    <th className="px-6 py-3 text-left text-white">Description</th>
                    <th className="px-6 py-3 text-left text-white">Usage</th>
                    <th className="px-6 py-3 text-left text-white">Expires</th>
                    <th className="px-6 py-3 text-left text-white">Status</th>
                    <th className="px-6 py-3 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon, index) => (
                    <tr key={coupon.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-mono font-bold" style={{color: '#D66238'}}>
                        {coupon.code}
                      </td>
                      <td className="px-6 py-4">
                        {coupon.discount_type === 'coins' 
                          ? <span className="text-amber-600 font-semibold">🪙 {coupon.discount_value} coins</span>
                          : coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}%`
                            : `$${coupon.discount_value.toFixed(2)}`
                        }
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {coupon.description || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {coupon.times_used || 0}
                        {coupon.max_uses ? ` / ${coupon.max_uses}` : ' / ∞'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {coupon.expiration_date 
                          ? new Date(coupon.expiration_date).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {coupon.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDeleteCoupon(coupon.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coupons.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No coupon codes created yet
                </div>
              )}
            </div>

            {/* Create Coupon Dialog */}
            {showCouponDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Create Coupon Code</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Coupon Code *</label>
                      <Input
                        value={couponForm.code}
                        onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                        placeholder="SAVE20"
                        className="font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Coupon Type *</label>
                      <select
                        value={couponForm.discount_type}
                        onChange={(e) => setCouponForm({...couponForm, discount_type: e.target.value})}
                        className="w-full p-2 border rounded"
                      >
                        <option value="coins">🪙 Free Coins</option>
                        <option value="percentage">Percentage Off (%)</option>
                        <option value="fixed">Fixed Amount Off ($)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {couponForm.discount_type === 'coins' ? 'Coins to Award *' : 'Discount Value *'}
                      </label>
                      <Input
                        type="number"
                        value={couponForm.discount_value}
                        onChange={(e) => setCouponForm({...couponForm, discount_value: parseFloat(e.target.value)})}
                        placeholder={couponForm.discount_type === 'coins' ? '100' : couponForm.discount_type === 'percentage' ? '20' : '5.00'}
                        step={couponForm.discount_type === 'coins' ? '1' : '0.01'}
                      />
                      {couponForm.discount_type === 'coins' && (
                        <p className="text-xs text-gray-500 mt-1">Users will receive this many coins when they redeem the coupon</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Description</label>
                      <Textarea
                        value={couponForm.description}
                        onChange={(e) => setCouponForm({...couponForm, description: e.target.value})}
                        placeholder={couponForm.discount_type === 'coins' ? 'Free 100 coins for new users!' : 'Black Friday Sale'}
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Max Uses (optional)</label>
                      <Input
                        type="number"
                        value={couponForm.max_uses || ''}
                        onChange={(e) => setCouponForm({...couponForm, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                        placeholder="Unlimited"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Expiration Date (optional)</label>
                      <Input
                        type="date"
                        value={couponForm.expiration_date}
                        onChange={(e) => setCouponForm({...couponForm, expiration_date: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowCouponDialog(false)}
                      className="flex-1 p-2 border rounded"
                    >
                      Cancel
                    </button>
                    <Button
                      onClick={handleCreateCoupon}
                      disabled={isSubmitting}
                      className="flex-1"
                      style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Coupon'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>


          <TabsContent value="mercantile">
            {/* Category Management Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #D66238'}}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold" style={{color: '#D66238'}}>📁 Categories</h3>
                  <p className="text-gray-600 text-sm">Manage mercantile categories</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSyncCategories}
                    variant="outline"
                    size="sm"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    🔄 Sync from Items
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({ name: '', display_name: '' });
                      setShowCategoryDialog(true);
                    }}
                    size="sm"
                    style={{backgroundColor: '#D66238', color: '#fff'}}
                  >
                    <Plus size={16} className="mr-1" /> Add Category
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {mercantileCategories.map(cat => (
                  <div key={cat.id} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                    <span className="font-medium capitalize">{cat.display_name || cat.name}</span>
                    <span className="text-xs text-gray-500">({mercantileItems.filter(i => i.category === cat.name).length} items)</span>
                    <button
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryForm({ name: cat.name, display_name: cat.display_name || cat.name });
                        setShowCategoryDialog(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {mercantileCategories.length === 0 && (
                  <p className="text-gray-500 text-sm">No categories yet. Click "Sync from Items" to create from existing items.</p>
                )}
              </div>
            </div>

            {/* Category Dialog */}
            {showCategoryDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                    {editingCategory ? 'Edit Category' : 'Add Category'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category Name (internal)</label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., buildings"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Display Name</label>
                      <input
                        type="text"
                        value={categoryForm.display_name}
                        onChange={(e) => setCategoryForm({...categoryForm, display_name: e.target.value})}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., Buildings & Structures"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCategoryDialog(false);
                        setEditingCategory(null);
                      }}
                      className="flex-1 p-2 border rounded"
                    >
                      Cancel
                    </button>
                    <Button
                      onClick={handleSaveCategory}
                      className="flex-1"
                      style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                    >
                      {editingCategory ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Items Management Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Mercantile Items</h3>
                  <p className="text-gray-600">Manage items users can purchase with coins</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSeedMercantile}
                    variant="outline"
                    className="flex items-center gap-2"
                    style={{borderColor: '#D66238', color: '#D66238'}}
                  >
                    🌱 Seed Default Items
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingMercantileItem(null);
                      setMercantileForm({
                        name: '',
                        description: '',
                        price: 0,
                        category: '',
                        subcategory: '',
                        image_url: '',
                        item_type: 'inventory',
                        inventory_key: '',
                        ranch_asset_key: '',
                        stock: null
                      });
                      setShowMercantileDialog(true);
                    }}
                    className="flex items-center gap-2"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    <Plus size={20} />
                    Add Item
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full">
                <thead style={{backgroundColor: '#6FCFD3'}}>
                  <tr>
                    <th className="px-4 py-3 text-left text-white">Image</th>
                    <th className="px-4 py-3 text-left text-white">Name</th>
                    <th className="px-4 py-3 text-left text-white">Price</th>
                    <th className="px-4 py-3 text-left text-white">Category</th>
                    <th className="px-4 py-3 text-left text-white">Stock</th>
                    <th className="px-4 py-3 text-left text-white">Status</th>
                    <th className="px-4 py-3 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mercantileItems.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-3">
                        <div className="relative group">
                          {item.image_url ? (
                            <img 
                              src={item.image_url} 
                              alt={item.name} 
                              className="w-12 h-12 object-cover rounded border"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                              No img
                            </div>
                          )}
                          <input
                            type="file"
                            accept={ACCEPTED_IMAGE_TYPES}
                            id={`merc-img-${item.id}`}
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              if (!isValidImageType(file)) {
                                toast.error('Please upload an image file');
                                return;
                              }
                              try {
                                toast.info('Processing image...');
                                const processedFile = await processImageForUpload(file, 8);
                                const formData = new FormData();
                                formData.append('file', processedFile);
                                const response = await api.post('/upload-media', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                const imageUrl = response.data.url;
                                const fullUrl = imageUrl.startsWith('/api/') 
                                  ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` 
                                  : imageUrl;
                                await handleUpdateMercantileItem(item.id, { image_url: fullUrl });
                                toast.success('Image uploaded!');
                              } catch (error) {
                                console.error('Upload error:', error);
                                toast.error(error.message || 'Failed to upload image');
                              }
                            }}
                          />
                          <button
                            onClick={() => document.getElementById(`merc-img-${item.id}`).click()}
                            className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center text-white text-xs"
                          >
                            📷
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold" style={{color: '#D66238'}}>{item.name}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[150px]">{item.description}</div>
                      </td>
                      <td className="px-4 py-3 font-bold" style={{color: '#6FCFD3'}}>
                        {item.price} 🪙
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="capitalize">{item.category}</span>
                        {item.subcategory && <span className="text-xs text-gray-400 block">{item.subcategory}</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {item.stock === -1 ? '∞' : item.stock}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleUpdateMercantileItem(item.id, { active: !item.active })}
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {item.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditMercantileItem(item)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit Item"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteMercantileItem(item.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mercantileItems.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No items created yet
                </div>
              )}
            </div>

            {showMercantileDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                    {editingMercantileItem ? 'Edit Mercantile Item' : 'Add Mercantile Item'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Item Name *</label>
                      <Input
                        value={mercantileForm.name}
                        onChange={(e) => setMercantileForm({...mercantileForm, name: e.target.value})}
                        placeholder="Premium Hay Bundle"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Description *</label>
                      <Textarea
                        value={mercantileForm.description}
                        onChange={(e) => setMercantileForm({...mercantileForm, description: e.target.value})}
                        placeholder="High-quality alfalfa hay for your mustangs"
                        rows={3}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Price (Coins) *</label>
                      <Input
                        type="number"
                        value={mercantileForm.price}
                        onChange={(e) => setMercantileForm({...mercantileForm, price: e.target.value})}
                        placeholder="100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Category *</label>
                      <select
                        value={mercantileForm.category}
                        onChange={(e) => setMercantileForm({...mercantileForm, category: e.target.value})}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select category</option>
                        <option value="supplies">Supplies</option>
                        <option value="feed">Feed</option>
                        <option value="equipment">Equipment</option>
                        <option value="decorations">Decorations</option>
                        <option value="grooming">Grooming</option>
                        <option value="health">Health & Care</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Item Image</label>
                      <div className="flex gap-2">
                        <Input
                          value={mercantileForm.image_url}
                          onChange={(e) => setMercantileForm({...mercantileForm, image_url: e.target.value})}
                          placeholder="Image URL or upload below..."
                          className="flex-1"
                        />
                      </div>
                      <div className="mt-2">
                        <input
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES}
                          id="mercantile-image-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (!isValidImageType(file)) {
                              toast.error('Please upload an image file');
                              return;
                            }
                            try {
                              toast.info('Processing image...');
                              const processedFile = await processImageForUpload(file, 8);
                              const formData = new FormData();
                              formData.append('file', processedFile);
                              const response = await api.post('/upload-media', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              const imageUrl = response.data.url;
                              // Convert to full URL
                              const fullUrl = imageUrl.startsWith('/api/') 
                                ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` 
                                : imageUrl;
                              setMercantileForm({...mercantileForm, image_url: fullUrl});
                              toast.success('Image uploaded!');
                            } catch (error) {
                              console.error('Upload error:', error);
                              toast.error(error.message || 'Failed to upload image');
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('mercantile-image-upload').click()}
                          className="gap-2"
                          style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                        >
                          📷 Upload Image
                        </Button>
                        {mercantileForm.image_url && (
                          <div className="mt-2">
                            <img 
                              src={mercantileForm.image_url} 
                              alt="Preview" 
                              className="w-20 h-20 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Stock (optional, leave empty for unlimited)</label>
                      <Input
                        type="number"
                        value={mercantileForm.stock || ''}
                        onChange={(e) => setMercantileForm({...mercantileForm, stock: e.target.value})}
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowMercantileDialog(false);
                        setEditingMercantileItem(null);
                        setMercantileForm({
                          name: '',
                          description: '',
                          price: 0,
                          category: '',
                          subcategory: '',
                          image_url: '',
                          item_type: 'inventory',
                          inventory_key: '',
                          ranch_asset_key: '',
                          stock: null
                        });
                      }}
                      className="flex-1 p-2 border rounded"
                    >
                      Cancel
                    </button>
                    <Button
                      onClick={handleCreateMercantileItem}
                      className="flex-1"
                      style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                    >
                      {editingMercantileItem ? 'Update Item' : 'Add Item'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="coin-activities">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Coin Earning Activities</h3>
                  <p className="text-gray-600">Manage ways users can earn coins</p>
                </div>
                <Button
                  onClick={() => setShowCoinActivityDialog(true)}
                  className="flex items-center gap-2"
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  <Plus size={20} />
                  Add Activity
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full">
                <thead style={{backgroundColor: '#6FCFD3'}}>
                  <tr>
                    <th className="px-6 py-3 text-left text-white">Activity</th>
                    <th className="px-6 py-3 text-left text-white">Type</th>
                    <th className="px-6 py-3 text-left text-white">Coins Reward</th>
                    <th className="px-6 py-3 text-left text-white">Description</th>
                    <th className="px-6 py-3 text-left text-white">Max/Day</th>
                    <th className="px-6 py-3 text-left text-white">Status</th>
                    <th className="px-6 py-3 text-left text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coinActivities.map((activity, index) => (
                    <tr key={activity.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-semibold" style={{color: '#D66238'}}>
                        {activity.activity_name}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono">
                        {activity.activity_type}
                      </td>
                      <td className="px-6 py-4 font-bold" style={{color: '#6FCFD3'}}>
                        +{activity.coins_reward} <img src="https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/12b5y0cy_B2C99D99-3648-4E18-8993-68DCE291F164.png" alt="Coin" className="w-4 h-4 inline object-contain" />
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {activity.description}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {activity.max_per_day ?? '∞'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          activity.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDeleteCoinActivity(activity.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coinActivities.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No activities created yet
                </div>
              )}
            </div>

            {showCoinActivityDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>Add Coin Activity</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Activity Name *</label>
                      <Input
                        value={coinActivityForm.activity_name}
                        onChange={(e) => setCoinActivityForm({...coinActivityForm, activity_name: e.target.value})}
                        placeholder="Daily Login"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Activity Type (unique identifier) *</label>
                      <Input
                        value={coinActivityForm.activity_type}
                        onChange={(e) => setCoinActivityForm({...coinActivityForm, activity_type: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                        placeholder="daily_login"
                      />
                      <p className="text-xs text-gray-500 mt-1">Use lowercase with underscores (e.g., daily_login, quiz_completion)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Coins Reward *</label>
                      <Input
                        type="number"
                        value={coinActivityForm.coins_reward}
                        onChange={(e) => setCoinActivityForm({...coinActivityForm, coins_reward: e.target.value})}
                        placeholder="10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Description *</label>
                      <Textarea
                        value={coinActivityForm.description}
                        onChange={(e) => setCoinActivityForm({...coinActivityForm, description: e.target.value})}
                        placeholder="Login to the platform every day"
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Max Per Day (optional, leave empty for unlimited)</label>
                      <Input
                        type="number"
                        value={coinActivityForm.max_per_day || ''}
                        onChange={(e) => setCoinActivityForm({...coinActivityForm, max_per_day: e.target.value})}
                        placeholder="Unlimited"
                      />
                      <p className="text-xs text-gray-500 mt-1">Set to 1 for once-daily activities</p>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowCoinActivityDialog(false)}
                      className="flex-1 p-2 border rounded"
                    >
                      Cancel
                    </button>
                    <Button
                      onClick={handleCreateCoinActivity}
                      className="flex-1"
                      style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                    >
                      Add Activity
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>


          <TabsContent value="users">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>User Management</h3>
                  <p className="text-gray-600">Manage user accounts and admin access. Total users: {users.length}</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      console.log('Create new admin button clicked');
                      setShowCreateUserDialog(true);
                    }}
                    style={{backgroundColor: '#D66238', color: 'white'}}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Admin
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('Refresh users button clicked');
                      fetchUsers();
                    }}
                    style={{backgroundColor: '#6FCFD3', color: 'white'}}
                  >
                    🔄 Refresh
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" 
                     style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
                <p style={{color: '#D66238'}}>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow" style={{border: '2px solid #6FCFD3'}}>
                <UsersIcon className="w-16 h-16 mx-auto mb-4 opacity-30" style={{color: '#D66238'}} />
                <p className="text-gray-600">No users found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-5 rounded-xl shadow hover:shadow-lg transition"
                    style={{backgroundColor: '#fff', border: '2px solid ' + (u.is_admin ? '#6FCFD3' : '#e5e7eb')}}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full border-2" style={{borderColor: '#6FCFD3'}} />
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold" style={{backgroundColor: '#6FCFD3'}}>
                          {u.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-lg" style={{color: '#D66238'}}>{u.name}</p>
                          {u.is_admin && (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-bold"
                              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                            >
                              <Shield className="w-3 h-3 inline mr-1" />
                              ADMIN
                            </span>
                          )}
                          {u.id === user.id && (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-bold"
                              style={{backgroundColor: '#D66238', color: '#fff'}}
                            >
                              YOU
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{u.email}</p>
                        <p className="text-xs text-gray-400">
                          Joined: {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {u.id !== user.id && (
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                          size="sm"
                          className="flex items-center gap-2"
                          style={{
                            backgroundColor: u.is_admin ? '#f59e0b' : '#6FCFD3',
                            color: '#fff'
                          }}
                        >
                          <Shield className="w-4 h-4" />
                          {u.is_admin ? 'Revoke Admin' : 'Make Admin'}
                        </Button>
                        <Button
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          size="sm"
                          variant="outline"
                          className="flex items-center gap-2"
                          style={{
                            borderColor: '#ef4444',
                            color: '#ef4444'
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    )}
                    
                    {u.id === user.id && (
                      <div className="px-4 py-2 rounded-lg" style={{backgroundColor: '#FCFCF8'}}>
                        <p className="text-sm font-medium" style={{color: '#D66238'}}>
                          This is your account
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* COMMUNITY MANAGEMENT TAB */}
          <TabsContent value="community">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Community Members</h3>
                  <p className="text-gray-600">Manage community profiles and members. Total: {communityMembers.length}</p>
                </div>
                <Button
                  onClick={fetchCommunityMembers}
                  style={{backgroundColor: '#6FCFD3', color: 'white'}}
                >
                  🔄 Refresh
                </Button>
              </div>
            </div>

            {communityLoading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" 
                     style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
                <p style={{color: '#D66238'}}>Loading community members...</p>
              </div>
            ) : communityMembers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow" style={{border: '2px solid #6FCFD3'}}>
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" style={{color: '#D66238'}} />
                <p className="text-gray-600">No community members found</p>
                <Button onClick={fetchCommunityMembers} className="mt-4" style={{backgroundColor: '#6FCFD3', color: 'white'}}>
                  Load Community Members
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {communityMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className="bg-white rounded-lg shadow p-4"
                    style={{border: '2px solid #6FCFD3'}}
                  >
                    <div className="flex items-start gap-3">
                      {member.photo_url ? (
                        <img 
                          src={member.photo_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${member.photo_url}` : member.photo_url} 
                          alt={member.display_name} 
                          className="w-16 h-16 rounded-full object-cover border-2"
                          style={{borderColor: '#6FCFD3'}}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{backgroundColor: '#6FCFD3'}}>
                          {member.display_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-lg truncate" style={{color: '#D66238'}}>{member.display_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-600">{member.state || 'No state'}</p>
                        {member.bio && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{member.bio}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Joined: {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Delete this member?')) return;
                          try {
                            await api.delete('/admin/community-members/' + member.user_id);
                            toast.success('Deleted');
                            fetchCommunityMembers();
                          } catch (e) {
                            toast.error('Failed');
                          }
                        }}
                        className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* APP SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <h3 className="text-2xl font-bold mb-4" style={{color: '#D66238'}}>App Logo Settings</h3>
              <p className="text-gray-600 mb-6">Upload or update the logo that appears across the entire application (navigation, landing page, etc.)</p>
              
              <div className="space-y-6">
                {/* Current Logo Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
                  <div className="border-2 border-dashed rounded-lg p-8 text-center" style={{borderColor: '#6FCFD3'}}>
                    {appSettings.logo_url ? (
                      <div className="space-y-4">
                        <img 
                          src={appSettings.logo_url} 
                          alt="App Logo" 
                          className="max-h-32 mx-auto object-contain"
                        />
                        <div className="flex justify-center gap-3">
                          <Button
                            onClick={() => logoInputRef.current?.click()}
                            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Change Logo
                          </Button>
                          <Button
                            onClick={handleRemoveLogo}
                            variant="outline"
                            style={{borderColor: '#D66238', color: '#D66238'}}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Logo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="w-16 h-16 mx-auto" style={{color: '#6FCFD3', opacity: 0.5}} />
                        <p className="text-gray-500">No logo uploaded yet</p>
                        <Button
                          onClick={() => logoInputRef.current?.click()}
                          style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </Button>
                      </div>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Recommended: PNG or SVG format, transparent background, max 8MB
                  </p>
                </div>

                {/* App Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Application Name</label>
                  <Input
                    value={appSettings.app_name || ''}
                    onChange={(e) => setAppSettings({...appSettings, app_name: e.target.value})}
                    placeholder="Wild Mustang Education & Adoption"
                    className="max-w-lg"
                  />
                  <Button
                    onClick={async () => {
                      try {
                        const response = await api.put('/admin/settings', {
                          app_name: appSettings.app_name
                        });
                        setAppSettings(response.data.settings);
                        toast.success('App name updated successfully!');
                      } catch (error) {
                        toast.error('Failed to update app name');
                      }
                    }}
                    className="mt-3"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save App Name
                  </Button>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div className="border rounded-lg p-4" style={{borderColor: '#6FCFD3'}}>
                    <p className="text-sm text-gray-600 mb-3">This is how your logo will appear in the navigation:</p>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                      {appSettings.logo_url ? (
                        <img src={appSettings.logo_url} alt="Logo" className="h-10 object-contain" />
                      ) : (
                        <div className="h-10 w-32 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                          No Logo
                        </div>
                      )}
                      <span className="font-semibold" style={{color: '#D66238'}}>
                        {appSettings.app_name || 'Wild Mustang Education & Adoption'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>


          {/* RANCH BLOCKS TAB */}
          <TabsContent value="ranch-blocks">
            <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{color: '#D66238'}}>Ranch Content Blocks (HTML/CSS/JS)</h2>
                <Button
                  onClick={() => {
                    setEditingRanchBlock(null);
                    setRanchBlockForm({
                      title: '',
                      html_content: '',
                      css_content: '',
                      js_content: '',
                      assets: [],
                      position: 'main',
                      is_active: true,
                      order: 0
                    });
                    setShowRanchBlockDialog(true);
                  }}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Block
                </Button>
              </div>

              {ranchBlocks.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No blocks created yet. Click "Add Block" to create your first custom HTML/JS block.</p>
              ) : (
                <div className="space-y-4">
                  {ranchBlocks.map((block) => (
                    <div key={block.id} className="border rounded-lg p-4" style={{borderColor: block.is_active ? '#6FCFD3' : '#ccc'}}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold" style={{color: '#D66238'}}>{block.title}</h3>
                            <span className={`px-2 py-1 text-xs rounded ${block.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {block.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                              {block.position}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            HTML: {block.html_content?.length || 0} chars | CSS: {block.css_content?.length || 0} chars | JS: {block.js_content?.length || 0} chars
                          </p>
                          <p className="text-xs text-gray-500 mb-1">
                            Assets: {Array.isArray(block.assets) ? block.assets.length : 0} file(s)
                          </p>
                          <p className="text-xs text-gray-500">Order: {block.order}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditRanchBlock(block)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteRanchBlock(block.id, block.title)}
                            style={{color: '#D66238'}}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* MEDIA LIBRARY TAB */}
          <TabsContent value="media-library">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold" style={{color: '#D66238'}}>Media Library</h3>
                  <p className="text-gray-600">Click the red DELETE button below any image to remove it</p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={mediaUploadInputRef}
                    onChange={handleMediaUpload}
                    accept={ACCEPTED_IMAGE_TYPES}
                    multiple
                    className="hidden"
                  />
                  <Button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sync-ai-images`, {
                          method: 'POST',
                          credentials: 'include'
                        });
                        const result = await response.json();
                        if (result.success) {
                          alert(`Scanned ${result.images_scanned} images, added ${result.images_added} new images to library`);
                          fetchMediaLibrary();
                        }
                      } catch (error) {
                        alert('Error syncing AI images: ' + error.message);
                      }
                    }}
                    variant="outline"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync AI Images
                  </Button>
                  <Button
                    onClick={() => mediaUploadInputRef.current?.click()}
                    disabled={uploadingMedia}
                    style={{backgroundColor: '#D66238', color: 'white'}}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingMedia ? 'Uploading...' : 'Upload Files'}
                  </Button>
                  {selectedMedia.length > 0 && (
                    <Button
                      onClick={handleDeleteSelectedMedia}
                      variant="outline"
                      className="text-red-500 border-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Selected ({selectedMedia.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-4 border" style={{borderColor: '#6FCFD3'}}>
                  <div className="text-2xl font-bold" style={{color: '#D66238'}}>{mediaLibrary.total}</div>
                  <div className="text-sm text-gray-600">Total Files</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border" style={{borderColor: '#6FCFD3'}}>
                  <div className="text-2xl font-bold" style={{color: '#D66238'}}>{selectedMedia.length}</div>
                  <div className="text-sm text-gray-600">Selected</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border" style={{borderColor: '#6FCFD3'}}>
                  <div className="text-2xl font-bold" style={{color: '#D66238'}}>
                    {mediaLibrary.items?.reduce((sum, item) => sum + (item.size || 0), 0) > 1024 * 1024 
                      ? `${(mediaLibrary.items?.reduce((sum, item) => sum + (item.size || 0), 0) / (1024 * 1024)).toFixed(1)} MB`
                      : `${(mediaLibrary.items?.reduce((sum, item) => sum + (item.size || 0), 0) / 1024).toFixed(1)} KB`
                    }
                  </div>
                  <div className="text-sm text-gray-600">Page Size</div>
                </div>
              </div>

              {/* Media Grid */}
              <div className="bg-white rounded-lg shadow p-4" style={{border: '2px solid #6FCFD3'}}>
                {mediaLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {/* Skeleton loading placeholders */}
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : mediaLibrary.items?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No media files uploaded yet. Click "Upload Files" to add images.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {mediaLibrary.items?.map((media) => (
                        <div 
                          key={media.filename}
                          className="relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#6FCFD3]"
                        >
                          {/* Image */}
                          <div 
                            className="aspect-square bg-gray-100 cursor-pointer"
                            onClick={() => toggleMediaSelection(media.filename)}
                          >
                            <img
                              src={media.thumb_url ? `${process.env.REACT_APP_BACKEND_URL}${media.thumb_url}` : getThumbUrl(media.url)}
                              alt={media.filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {/* Selection indicator */}
                            {selectedMedia.includes(media.filename) && (
                              <div className="absolute inset-0 bg-[#D66238]/30 border-4 border-[#D66238]">
                                <Check className="absolute top-2 left-2 w-6 h-6 text-white bg-[#D66238] rounded-full p-1" />
                              </div>
                            )}
                          </div>
                          
                          {/* Delete button below image */}
                          <div className="p-2 bg-gray-50 flex justify-between items-center">
                            <span className="text-xs text-gray-500 truncate flex-1 mr-2">
                              {media.size_human || ''}
                            </span>
                            <button
                              onClick={() => handleDeleteMedia(media.filename)}
                              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    {mediaLibrary.total > 50 && (
                      <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t">
                        <Button
                          variant="outline"
                          disabled={mediaPage === 0}
                          onClick={() => fetchMediaLibrary(mediaPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-gray-600">
                          Page {mediaPage + 1} of {Math.ceil(mediaLibrary.total / 50)}
                        </span>
                        <Button
                          variant="outline"
                          disabled={(mediaPage + 1) * 50 >= mediaLibrary.total}
                          onClick={() => fetchMediaLibrary(mediaPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Help Text */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p><strong>Tips:</strong></p>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li>Click on images to select them for bulk deletion</li>
                  <li>Hover over images to copy URL or delete individually</li>
                  <li>Supports JPG, PNG, GIF, WEBP, and iPhone HEIC photos</li>
                  <li>Files are automatically compressed to 8MB max</li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* Database Backups Tab */}
          <TabsContent value="backups">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Database Backups</h3>
                <p className="text-gray-600">Manage automatic and manual database backups to prevent data loss</p>
              </div>

              {/* Backup Info Banner */}
              <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-blue-900 mb-1">Automatic Backups Configured</p>
                    <p className="text-blue-800">
                      • Backups run daily at 2:00 AM<br/>
                      • Last 7 days of backups are retained<br/>
                      • Backups are stored in persistent storage (/app/backend/backups/)<br/>
                      • All uploaded images are permanently stored (/app/backend/uploads/)
                    </p>
                  </div>
                </div>
              </div>

              {/* Create Backup Button */}
              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={handleCreateBackup}
                  disabled={creatingBackup}
                  style={{backgroundColor: '#D66238', color: '#fff'}}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {creatingBackup ? 'Creating Backup...' : 'Create Manual Backup'}
                </Button>
                <Button
                  onClick={fetchBackups}
                  disabled={loadingBackups}
                  variant="outline"
                  style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                >
                  {loadingBackups ? 'Loading...' : 'Refresh List'}
                </Button>
                <Button
                  onClick={handleMigrateMedia}
                  disabled={migratingMedia}
                  variant="outline"
                  style={{borderColor: '#D66238', color: '#D66238'}}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {migratingMedia ? 'Migrating...' : 'Migrate Media to DB'}
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Regenerating thumbnails with higher quality...');
                      const response = await api.post('/admin/regenerate-thumbnails');
                      toast.success(`Regenerated ${response.data.regenerated} thumbnails! ${response.data.failed > 0 ? `(${response.data.failed} failed)` : ''}`);
                    } catch (error) {
                      toast.error('Failed to regenerate thumbnails: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  variant="outline"
                  style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Thumbnails
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Exporting all user data...');
                      const response = await api.post('/admin/export-all-users');
                      const data = response.data;
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `user-data-backup-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`Exported ${data.users?.length || 0} users with all their data!`);
                    } catch (error) {
                      toast.error('Failed to export user data');
                    }
                  }}
                  variant="outline"
                  style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All Users
                </Button>
                <Button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    input.onchange = async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        toast.info('Importing user data...');
                        const text = await file.text();
                        const data = JSON.parse(text);
                        const response = await api.post('/admin/import-all-users', data);
                        const imported = response.data.imported;
                        toast.success(`Imported: ${imported.users || 0} users, ${imported.user_mustangs || 0} horses`);
                        fetchUsers();
                      } catch (error) {
                        toast.error('Failed to import: ' + (error.message || 'Invalid file'));
                      }
                    };
                    input.click();
                  }}
                  variant="outline"
                  style={{borderColor: '#D66238', color: '#D66238'}}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Users
                </Button>
              </div>

              {/* Backups List */}
              <div className="bg-white rounded-lg border-2" style={{borderColor: '#6FCFD3'}}>
                <div className="p-4 border-b-2" style={{borderColor: '#6FCFD3'}}>
                  <h4 className="font-bold text-lg" style={{color: '#D66238'}}>
                    Available Backups ({backups.length})
                  </h4>
                </div>
                
                {loadingBackups ? (
                  <div className="p-8 text-center">
                    <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto" 
                         style={{borderColor: '#6FCFD3', borderTopColor: 'transparent'}}></div>
                    <p className="mt-2 text-gray-600">Loading backups...</p>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Save className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No backups available</p>
                    <p className="text-sm mt-1">Create your first backup using the button above</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {backups.map((backup) => (
                      <div key={backup.filename} className="p-4 hover:bg-gray-50 transition">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{backup.filename}</p>
                            <div className="flex gap-4 mt-1 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {backup.created_human}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="w-4 h-4" />
                                {backup.size_human}
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleRestoreBackup(backup.filename)}
                            variant="outline"
                            size="sm"
                            style={{borderColor: '#D66238', color: '#D66238'}}
                          >
                            Restore
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Warning Section */}
              <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-900 mb-1">⚠️ Important Notes</p>
                    <p className="text-amber-800">
                      • Restoring a backup will <strong>overwrite all current data</strong><br/>
                      • Always create a fresh backup before restoring<br/>
                      • The page will reload after successful restoration<br/>
                      • Backups include all database collections (users, mustangs, settings, etc.)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="ranchette">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Ranchette Preview</h3>
                  <p className="text-gray-600">
                    Build and preview the Ranchette game. Use "Ranch Blocks" tab to add/edit content.
                  </p>
                </div>
                <Button
                  onClick={() => window.open('/ranchette', '_blank')}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  Open in New Tab
                </Button>
              </div>
            </div>

            {/* Embedded Ranchette Preview */}
            <div className="bg-white rounded-lg shadow overflow-hidden" style={{border: '2px solid #6FCFD3'}}>
              <iframe
                src="/ranchette"
                title="Ranchette Preview"
                className="w-full"
                style={{
                  height: '800px',
                  border: 'none'
                }}
              />
            </div>

            {/* Quick Info */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 Quick Tips:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Use the "Ranch Blocks" tab to add HTML/CSS/JS content to Ranchette</li>
                <li>• Blocks can be positioned: top, main, sidebar, or bottom</li>
                <li>• The ranch game iframe is always displayed in the main area</li>
                <li>• Click "Open in New Tab" for full-screen editing</li>
              </ul>
            </div>
          </TabsContent>

          {/* HERD SETTINGS TAB */}
          <TabsContent value="herd-settings">
            <div className="space-y-8">
              {/* Header */}
              <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold" style={{color: '#D66238'}}>Herd Module Settings</h3>
                    <p className="text-gray-600">Customize the Daily Care and Training checklists for the Herd module</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleResetHerdSettings}
                      variant="outline"
                      style={{borderColor: '#D66238', color: '#D66238'}}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset to Defaults
                    </Button>
                  </div>
                </div>
                {/* Info Banner */}
                {(!herdSettings?.daily_care_categories?.length || !herdSettings?.training_categories?.length) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Tip:</strong> Click "Reset to Defaults" to load the standard Daily Care and Training categories that you can then customize.
                    </p>
                  </div>
                )}
              </div>

              {/* Daily Care Categories */}
              <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold" style={{color: '#D66238'}}>Daily Care Categories ({herdSettings?.daily_care_categories?.length || 0})</h4>
                  <Button onClick={handleAddDailyCareCategory} style={{backgroundColor: '#D66238', color: 'white'}}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  These categories appear in the "Daily Care" tab of each horse card in the Herd module.
                </p>
                
                <div className="space-y-3">
                  {herdSettings?.daily_care_categories?.map((category, index) => (
                    <div key={category.key} className="border rounded-lg p-4" style={{borderColor: '#6FCFD3'}}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {category.icon_url && (
                            <img src={getImageUrl(category.icon_url)} alt={category.label} className="w-8 h-8 object-contain" />
                          )}
                          <div>
                            <span className="font-semibold" style={{color: '#D66238'}}>{category.label}</span>
                            <span className="text-xs text-gray-500 ml-2">({category.key})</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditDailyCareCategory(category, index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteDailyCareCategory(index)} className="text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {category.items.map((item) => (
                          <span key={item.key} className="px-2 py-1 bg-gray-100 rounded text-sm">
                            {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(!herdSettings?.daily_care_categories || herdSettings.daily_care_categories.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No daily care categories configured. Click "Add Category" to create one.
                    </div>
                  )}
                </div>
              </div>

              {/* Training Categories */}
              <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold" style={{color: '#D66238'}}>Training Categories (Gentling Checklist)</h4>
                  <Button onClick={handleAddTrainingCategory} style={{backgroundColor: '#D66238', color: 'white'}}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  These categories appear in the "Training" tab of each horse card in the Herd module.
                </p>
                
                <div className="space-y-3">
                  {herdSettings?.training_categories?.map((category, index) => (
                    <div key={category.key} className="border rounded-lg p-4" style={{borderColor: '#6FCFD3'}}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold" style={{color: '#D66238'}}>{category.label}</span>
                          <span className="text-xs text-gray-500 ml-2">({category.key})</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditTrainingCategory(category, index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteTrainingCategory(index)} className="text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {category.items.map((item) => (
                          <span key={item.key} className="px-2 py-1 bg-gray-100 rounded text-sm">
                            {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(!herdSettings?.training_categories || herdSettings.training_categories.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No training categories configured. Click "Add Category" to create one.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* DATA MIGRATION TAB */}
          <TabsContent value="data-migration">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>Data Migration</h3>
              <p className="text-gray-600">Export data from preview and import it to production to sync both environments.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Export Section */}
              <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                  <Download className="w-5 h-5 inline mr-2" />
                  Export Data
                </h4>
                <p className="text-gray-600 mb-4">
                  Download all app data as a JSON file. Use this in the preview environment to export your data.
                </p>
                <Button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-all-data`, {
                        credentials: 'include'
                      });
                      const result = await response.json();
                      if (result.success) {
                        // Create downloadable file
                        const dataStr = JSON.stringify(result.data, null, 2);
                        const blob = new Blob([dataStr], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `wild_app_migration_${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        alert(`Successfully exported ${result.total_documents} documents from ${result.collections_exported.length} collections!`);
                      } else {
                        alert('Export failed');
                      }
                    } catch (error) {
                      alert('Export error: ' + error.message);
                    }
                  }}
                  className="w-full"
                  style={{backgroundColor: '#6FCFD3'}}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All Data
                </Button>
              </div>
              
              {/* Import Section */}
              <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
                <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                  <Upload className="w-5 h-5 inline mr-2" />
                  Import Data
                </h4>
                <p className="text-gray-600 mb-4">
                  Upload a migration JSON file to import data. Use this in the production environment to import data from preview.
                </p>
                <input
                  type="file"
                  accept=".json"
                  id="migration-file-input"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    try {
                      const text = await file.text();
                      const data = JSON.parse(text);
                      
                      if (!window.confirm(`Import ${Object.keys(data).length} collections? This will replace existing data in those collections.`)) {
                        return;
                      }
                      
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-all-data`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ data, clear_existing: true })
                      });
                      
                      const result = await response.json();
                      if (result.success) {
                        alert(`Successfully imported ${result.total_documents} documents!`);
                        window.location.reload();
                      } else {
                        alert('Import failed: ' + JSON.stringify(result));
                      }
                    } catch (error) {
                      alert('Import error: ' + error.message);
                    }
                    e.target.value = '';
                  }}
                />
                <Button
                  onClick={() => document.getElementById('migration-file-input').click()}
                  className="w-full mb-3"
                  style={{backgroundColor: '#D66238'}}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Migration File
                </Button>
                
                {/* One-Click Auto Import */}
                <Button
                  onClick={async () => {
                    if (!window.confirm('This will import all data from the bundled migration file. This will REPLACE existing data. Continue?')) {
                      return;
                    }
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/auto-import-from-preview`, {
                        method: 'POST',
                        credentials: 'include'
                      });
                      const result = await response.json();
                      if (result.success) {
                        alert(`✅ Successfully imported ${result.total_imported} documents!\n\nImported collections:\n${Object.entries(result.imported).map(([k,v]) => `• ${k}: ${v}`).join('\n')}`);
                        window.location.reload();
                      } else {
                        alert('Import had errors: ' + JSON.stringify(result.errors));
                      }
                    } catch (error) {
                      alert('Auto-import error: ' + error.message);
                    }
                  }}
                  className="w-full"
                  variant="outline"
                  style={{borderColor: '#10b981', color: '#10b981'}}
                >
                  🚀 One-Click Auto Import (After Deploy)
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Uses bundled migration data. Only works after deployment.
                </p>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="bg-amber-50 rounded-lg p-6 mt-6" style={{border: '2px solid #EEC474'}}>
              <h4 className="text-lg font-bold mb-3" style={{color: '#D66238'}}>
                📋 How to Sync Preview → Production
              </h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li><strong>In Preview:</strong> Click "Export All Data" to download your data as a JSON file</li>
                <li><strong>In Production:</strong> Open the deployed app and go to Admin Panel → Data Migration</li>
                <li><strong>In Production:</strong> Click "Import Migration File" and select the downloaded JSON file</li>
                <li>Your horses, ranch assets, mercantile items, and settings will be synced!</li>
              </ol>
              <div className="mt-4 p-3 bg-white rounded" style={{border: '1px solid #6FCFD3'}}>
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> Import will replace existing data in the target collections. User accounts and sessions are not included in the migration to prevent conflicts.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* COIN MANAGER TAB */}
          <TabsContent value="coin-manager">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #EEC474'}}>
              <h3 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>💰 Coin Manager</h3>
              <p className="text-gray-600">Add coins to user accounts for testing or rewards.</p>
            </div>
            
            {/* Quick Add to Self */}
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                ⚡ Quick Add to My Account
              </h4>
              <div className="flex flex-wrap gap-3">
                {[100, 500, 1000, 5000].map(amount => (
                  <Button
                    key={amount}
                    onClick={async () => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/add-coins-to-self`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ amount, reason: `Quick add ${amount} coins` })
                        });
                        const result = await response.json();
                        if (result.success) {
                          toast.success(`Added ${amount} coins! New balance: ${result.new_balance}`);
                        } else {
                          toast.error(result.detail || 'Failed to add coins');
                        }
                      } catch (error) {
                        toast.error('Error: ' + error.message);
                      }
                    }}
                    style={{backgroundColor: '#EEC474', color: '#372F2F'}}
                  >
                    +{amount} 🪙
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Add to Specific User */}
            <div className="bg-white rounded-lg shadow p-6" style={{border: '2px solid #6FCFD3'}}>
              <h4 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                👤 Add Coins to User
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Select User</label>
                  <select
                    id="coin-user-select"
                    className="w-full p-2 border rounded"
                    defaultValue=""
                  >
                    <option value="">-- Select a user --</option>
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users-list`, {
                          credentials: 'include'
                        });
                        const result = await response.json();
                        const select = document.getElementById('coin-user-select');
                        select.innerHTML = '<option value="">-- Select a user --</option>';
                        result.users.forEach(u => {
                          const opt = document.createElement('option');
                          opt.value = u.id;
                          opt.textContent = `${u.email || u.name || 'Unknown'} (${u.ranch_coins || 0} coins)${u.is_admin ? ' [ADMIN]' : ''}`;
                          select.appendChild(opt);
                        });
                        toast.success(`Loaded ${result.users.length} users`);
                      } catch (error) {
                        toast.error('Failed to load users');
                      }
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Load Users
                  </Button>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Amount</label>
                  <Input
                    type="number"
                    id="coin-amount-input"
                    placeholder="Enter amount"
                    defaultValue="100"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Reason (optional)</label>
                  <Input
                    type="text"
                    id="coin-reason-input"
                    placeholder="e.g., Contest winner, Bug compensation"
                  />
                </div>
                
                <Button
                  onClick={async () => {
                    const userId = document.getElementById('coin-user-select').value;
                    const amount = parseInt(document.getElementById('coin-amount-input').value) || 0;
                    const reason = document.getElementById('coin-reason-input').value || 'Admin grant';
                    
                    if (!userId) {
                      toast.error('Please select a user');
                      return;
                    }
                    if (amount <= 0) {
                      toast.error('Amount must be positive');
                      return;
                    }
                    
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/add-coins`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ user_id: userId, amount, reason })
                      });
                      const result = await response.json();
                      if (result.success) {
                        toast.success(`Added ${amount} coins to ${result.email}! New balance: ${result.new_balance}`);
                        // Refresh user list
                        document.querySelector('[data-refresh-users]')?.click();
                      } else {
                        toast.error(result.detail || 'Failed to add coins');
                      }
                    } catch (error) {
                      toast.error('Error: ' + error.message);
                    }
                  }}
                  className="w-full"
                  style={{backgroundColor: '#D66238'}}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Add Coins to User
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* SUBSCRIPTIONS TAB */}
          <TabsContent value="subscriptions">
            <div className="bg-white rounded-lg shadow p-6 mb-6" style={{border: '2px solid #6FCFD3'}}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-2xl font-bold" style={{color: '#D66238'}}>Subscription Management</h3>
                  <p className="text-gray-600">View and manage user subscriptions</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={fetchSubscriptions}
                    disabled={loadingSubscriptions}
                    variant="outline"
                    style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingSubscriptions ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    onClick={handleFixAllSubscriptions}
                    variant="outline"
                    style={{borderColor: '#D66238', color: '#D66238'}}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Fix All
                  </Button>
                </div>
              </div>

              {/* Grant Subscription Form */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-6">
                <h4 className="font-bold text-green-800 mb-3">Grant New Subscription</h4>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-green-700 mb-1">User Email</label>
                    <Input
                      type="email"
                      value={grantSubscriptionEmail}
                      onChange={(e) => setGrantSubscriptionEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="border-green-300"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-sm font-medium text-green-700 mb-1">Days</label>
                    <Input
                      type="number"
                      value={grantSubscriptionDays}
                      onChange={(e) => setGrantSubscriptionDays(parseInt(e.target.value) || 30)}
                      min="1"
                      className="border-green-300"
                    />
                  </div>
                  <Button
                    onClick={handleGrantSubscription}
                    style={{backgroundColor: '#22c55e'}}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Grant Access
                  </Button>
                </div>
              </div>

              {/* Subscriptions List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-700">All Subscriptions ({subscriptions.length})</h4>
                  <div className="flex gap-2 text-sm">
                    <span className="px-2 py-1 rounded bg-green-100 text-green-800">
                      ✓ Active: {subscriptions.filter(s => s.is_active && (!s.valid_until || new Date(s.valid_until) > new Date())).length}
                    </span>
                    <span className="px-2 py-1 rounded bg-red-100 text-red-800">
                      ✗ Expired/Inactive: {subscriptions.filter(s => !s.is_active || (s.valid_until && new Date(s.valid_until) < new Date())).length}
                    </span>
                  </div>
                </div>
                
                {loadingSubscriptions ? (
                  <div className="text-center py-8 text-gray-500">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    <p>Loading subscriptions...</p>
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No subscriptions found. Click Refresh to load.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left p-3 border-b">User</th>
                          <th className="text-left p-3 border-b">Status</th>
                          <th className="text-left p-3 border-b">Valid Until</th>
                          <th className="text-left p-3 border-b">Created</th>
                          <th className="text-left p-3 border-b">Source</th>
                          <th className="text-center p-3 border-b">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map((sub, index) => {
                          const validUntil = sub.valid_until ? new Date(sub.valid_until) : null;
                          const isExpired = validUntil && validUntil < new Date();
                          const isActive = sub.is_active && !isExpired;
                          
                          return (
                            <tr key={`${sub.id}-${index}`} className={`border-b hover:bg-gray-50 ${!isActive ? 'bg-red-50' : ''}`}>
                              <td className="p-3">
                                <div className="font-medium">{sub.user_email}</div>
                                <div className="text-xs text-gray-500">{sub.user_name}</div>
                                <div className="text-xs text-gray-400 font-mono">{sub.user_id?.substring(0, 8)}...</div>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {isActive ? '✓ Active' : isExpired ? '✗ Expired' : '✗ Inactive'}
                                </span>
                                {sub.admin_granted && (
                                  <span className="ml-2 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                    Admin Granted
                                  </span>
                                )}
                              </td>
                              <td className="p-3">
                                {validUntil ? (
                                  <div>
                                    <div className={isExpired ? 'text-red-600 font-bold' : ''}>
                                      {validUntil.toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {validUntil.toLocaleTimeString()}
                                    </div>
                                    {isExpired && (
                                      <div className="text-xs text-red-500 font-medium">
                                        Expired {Math.floor((new Date() - validUntil) / (1000*60*60*24))} days ago
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">Not set</span>
                                )}
                              </td>
                              <td className="p-3 text-sm text-gray-600">
                                {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  sub.source?.includes('legacy') ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100'
                                }`}>
                                  {sub.source === 'virtual_corral_subscriptions' ? 'Stripe' : 'Legacy'}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex justify-center gap-2 flex-wrap">
                                  {!isActive ? (
                                    <Button
                                      size="sm"
                                      onClick={() => handleExtendSubscription(sub.user_id, sub.user_email, 30)}
                                      style={{backgroundColor: '#22c55e'}}
                                      title="Reactivate with 30 days"
                                    >
                                      Reactivate
                                    </Button>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        onClick={() => handleExtendSubscription(sub.user_id, sub.user_email, 30)}
                                        style={{backgroundColor: '#22c55e'}}
                                        title="Extend 30 days"
                                      >
                                        +30d
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handleExtendSubscription(sub.user_id, sub.user_email, 365)}
                                        style={{backgroundColor: '#6FCFD3'}}
                                        title="Extend 1 year"
                                      >
                                        +1yr
                                      </Button>
                                    </>
                                  )}
                                  {isActive && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRevokeSubscription(sub.user_id, sub.user_email)}
                                      style={{borderColor: '#ef4444', color: '#ef4444'}}
                                      title="Revoke"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>


        </Tabs>
      </div>

      {/* Module Create/Edit Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingModule ? 'Edit Module' : 'Create New Module'}
            </DialogTitle>
            <DialogDescription>
              {editingModule ? 'Update module details' : 'Create a new quiz module'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Title
              </label>
              <Input
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                placeholder="e.g., Nutrition & Feeding"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Description
              </label>
              <Textarea
                value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                placeholder="Brief description of what this module covers"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Order
              </label>
              <Input
                type="number"
                value={moduleForm.order}
                onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowModuleDialog(false);
                  setEditingModule(null);
                  setModuleForm({ title: '', description: '', order: 0 });
                }}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={editingModule ? handleUpdateModule : handleCreateModule}
                disabled={isSubmitting}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Saving...' : (editingModule ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Create/Edit Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog} modal={false}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </DialogTitle>
            <DialogDescription>
              {selectedModule?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Question
              </label>
              <Textarea
                value={questionForm.question}
                onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                placeholder="Enter the question"
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Section
              </label>
              <select
                value={questionForm.section}
                onChange={(e) => setQuestionForm({ ...questionForm, section: e.target.value })}
                className="w-full p-2 border rounded-md"
                style={{borderColor: '#E0E0E0'}}
              >
                {/* Show all quiz module titles as section options */}
                {modules.map(module => (
                  <option key={module.id} value={module.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}>
                    {module.title}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Answer Options (2-4 options, drag to reorder)
              </label>
              <DndContext
                sensors={useSensors(
                  useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
                  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
                )}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (over && active.id !== over.id) {
                    const oldIndex = questionForm.options.findIndex((_, i) => `option-${i}` === active.id);
                    const newIndex = questionForm.options.findIndex((_, i) => `option-${i}` === over.id);
                    const newOptions = arrayMove(questionForm.options, oldIndex, newIndex);
                    
                    // Update correct answer index if it was moved
                    let newCorrect = questionForm.correct;
                    if (questionForm.correct === oldIndex) {
                      newCorrect = newIndex;
                    } else if (oldIndex < questionForm.correct && newIndex >= questionForm.correct) {
                      newCorrect = questionForm.correct - 1;
                    } else if (oldIndex > questionForm.correct && newIndex <= questionForm.correct) {
                      newCorrect = questionForm.correct + 1;
                    }
                    
                    setQuestionForm({ ...questionForm, options: newOptions, correct: newCorrect });
                  }
                }}
              >
                <SortableContext items={questionForm.options.map((_, i) => `option-${i}`)} strategy={verticalListSortingStrategy}>
                  {questionForm.options.map((option, idx) => (
                    <SortableAnswerOption
                      key={`option-${idx}`}
                      id={`option-${idx}`}
                      option={option}
                      idx={idx}
                      questionForm={questionForm}
                      setQuestionForm={setQuestionForm}
                      optionsCount={questionForm.options.length}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {questionForm.options.length < 4 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQuestionForm({
                      ...questionForm,
                      options: [...questionForm.options, { text: '', photo_url: '' }]
                    });
                  }}
                  className="mt-2"
                >
                  + Add Option
                </Button>
              )}
              <p className="text-xs text-gray-500 mt-1">Minimum 2 options, maximum 4. Each option needs either text or a photo.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Explanation
              </label>
              <Textarea
                value={questionForm.explanation}
                onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                placeholder="Explain why the correct answer is correct"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Question Photo (Optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 8 * 1024 * 1024) {
                      alert('Image size must be less than 8MB');
                      e.target.value = '';
                      return;
                    }
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setQuestionForm({...questionForm, photo_url: reader.result});
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              />
              <p className="text-xs text-gray-500 mt-1">Upload an image to display with this question (max 8MB)</p>
              
              {/* Image Preview */}
              {questionForm.photo_url && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-2">Preview:</p>
                  <div className="relative w-full max-w-md rounded-lg overflow-hidden border-2 border-gray-300">
                    <img
                      src={questionForm.photo_url}
                      alt="Question preview"
                      className="w-full h-auto object-contain max-h-48"
                    />
                    <button
                      type="button"
                      onClick={() => setQuestionForm({...questionForm, photo_url: ''})}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowQuestionDialog(false);
                  setEditingQuestion(null);
                  setQuestionForm({
                    question: '',
                    options: [
                      { text: '', photo_url: '' },
                      { text: '', photo_url: '' }
                    ],
                    correct: [0],
                    explanation: '',
                    order: 0,
                    photo_url: ''
                  });
                }}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={editingQuestion ? handleUpdateQuestion : handleAddQuestion}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingQuestion ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Study Guide / Outline Edit Dialog */}
      <Dialog open={showOutlineDialog} onOpenChange={setShowOutlineDialog}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              Edit Study Guide - {selectedModule?.title}
            </DialogTitle>
            <DialogDescription>
              Create a printable study guide for this module. Each line will be a separate item.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm" style={{color: '#D66238'}}>
                {outlineContent.length} items in study guide
              </p>
              <Button
                onClick={addOutlineItem}
                size="sm"
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {outlineContent.map((item, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <span className="text-xs w-8 text-center" style={{color: '#6FCFD3'}}>
                    {index + 1}.
                  </span>
                  <Input
                    value={item}
                    onChange={(e) => updateOutlineItem(index, e.target.value)}
                    placeholder="Study guide item..."
                    className="flex-1"
                  />
                  <Button
                    onClick={() => removeOutlineItem(index)}
                    size="sm"
                    variant="ghost"
                    style={{color: '#D66238'}}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {outlineContent.length === 0 && (
              <div className="text-center py-8" style={{color: '#D66238', opacity: 0.5}}>
                <p>No study guide items yet.</p>
                <p className="text-sm">Click "Add Item" to start building the study guide.</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 justify-end pt-4 border-t" style={{borderColor: '#6FCFD3'}}>
              <Button
                onClick={() => {
                  setShowOutlineDialog(false);
                  setOutlineContent([]);
                }}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSaveOutline}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Study Guide
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resource Create/Edit Dialog */}
      <Dialog open={showResourceDialog} onOpenChange={setShowResourceDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingResource ? 'Edit Resource' : 'Add New Resource'}
            </DialogTitle>
            <DialogDescription>
              Add links to helpful websites, training materials, or official resources.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Title
              </label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                placeholder="e.g., BLM Official Resources"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Description
              </label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                placeholder="Brief description of this resource"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Link (URL)
              </label>
              <Input
                value={resourceForm.link}
                onChange={(e) => setResourceForm({ ...resourceForm, link: e.target.value })}
                placeholder="https://example.com"
                type="url"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Category
              </label>
              <Input
                value={resourceForm.category}
                onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}
                placeholder="e.g., training, official, health"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Order
              </label>
              <Input
                type="number"
                value={resourceForm.order}
                onChange={(e) => setResourceForm({ ...resourceForm, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowResourceDialog(false);
                  setEditingResource(null);
                  setResourceForm({ title: '', description: '', link: '', category: 'general', order: 0 });
                }}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={editingResource ? handleUpdateResource : handleCreateResource}
                disabled={isSubmitting}
                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Saving...' : (editingResource ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trainer Create/Edit Dialog */}
      <Dialog open={showTrainerDialog} onOpenChange={setShowTrainerDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}
            </DialogTitle>
            <DialogDescription>
              {editingTrainer ? 'Update trainer information' : 'Add a new trainer to the platform'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Name*
              </label>
              <Input
                value={trainerForm.name}
                onChange={(e) => setTrainerForm({ ...trainerForm, name: e.target.value })}
                placeholder="Trainer name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Trainer Photo
              </label>
              <input
                type="file"
                ref={trainerPhotoInputRef}
                className="hidden"
                accept={ACCEPTED_IMAGE_TYPES}
                onChange={handleTrainerPhotoUpload}
              />
              <div className="space-y-3">
                {trainerForm.photo_url && (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2" style={{borderColor: '#6FCFD3'}}>
                    <img 
                      src={getImageUrl(trainerForm.photo_url)} 
                      alt="Trainer preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setTrainerForm({ ...trainerForm, photo_url: '' })}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => trainerPhotoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  {uploadingPhoto ? 'Processing...' : (trainerForm.photo_url ? 'Change Photo' : 'Upload Photo')}
                </Button>
                <p className="text-xs text-gray-500">Supports iPhone photos (HEIC), JPG, PNG, GIF, WEBP. Auto-compresses to 8MB.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Bio*
              </label>
              <Textarea
                value={trainerForm.bio}
                onChange={(e) => setTrainerForm({ ...trainerForm, bio: e.target.value })}
                placeholder="Brief bio about the trainer..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                  City
                </label>
                <Input
                  value={trainerForm.city}
                  onChange={(e) => setTrainerForm({ ...trainerForm, city: e.target.value })}
                  placeholder="e.g., Austin"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                  State
                </label>
                <Input
                  value={trainerForm.state}
                  onChange={(e) => setTrainerForm({ ...trainerForm, state: e.target.value })}
                  placeholder="e.g., TX"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Specialties
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  placeholder="e.g., Groundwork, Colt Starting"
                />
                <Button
                  type="button"
                  onClick={addSpecialty}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {trainerForm.specialties.map((specialty, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full flex items-center gap-2"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    {specialty}
                    <button
                      onClick={() => removeSpecialty(specialty)}
                      className="hover:opacity-75"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Availability
              </label>
              <select
                value={trainerForm.availability}
                onChange={(e) => setTrainerForm({ ...trainerForm, availability: e.target.value })}
                className="w-full p-2 border rounded"
              >
                <option value="Available">Available</option>
                <option value="Limited">Limited</option>
                <option value="Unavailable">Unavailable</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Response Time
              </label>
              <select
                value={trainerForm.response_time}
                onChange={(e) => setTrainerForm({ ...trainerForm, response_time: e.target.value })}
                className="w-full p-2 border rounded"
              >
                <option value="Within 1 hour">Within 1 hour</option>
                <option value="Within 4 hours">Within 4 hours</option>
                <option value="Within 12 hours">Within 12 hours</option>
                <option value="Within 24 hours">Within 24 hours</option>
                <option value="Within 48 hours">Within 48 hours</option>
                <option value="3-5 business days">3-5 business days</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              onClick={() => {
                setShowTrainerDialog(false);
                setEditingTrainer(null);
                setTrainerForm({ name: '', photo_url: '', bio: '', city: '', state: '', specialties: [], availability: 'Available', response_time: 'Within 24 hours' });
              }}
              disabled={isSubmitting}
              style={{
                borderColor: '#D66238',
                color: '#D66238'
              }}
              variant="outline"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={editingTrainer ? handleUpdateTrainer : handleCreateTrainer}
              disabled={isSubmitting}
              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : (editingTrainer ? 'Update' : 'Create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Plan Create/Edit Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingPlan ? 'Edit Pricing Plan' : 'Create New Pricing Plan'}
            </DialogTitle>
            <DialogDescription>
              {editingPlan ? 'Update pricing plan details' : 'Create a new subscription plan'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Plan Type* {editingPlan && <span className="text-xs text-gray-500">(Cannot be changed)</span>}
              </label>
              {editingPlan ? (
                <Input
                  value={planForm.plan_type}
                  disabled
                  className="bg-gray-100"
                />
              ) : (
                <select
                  value={planForm.plan_type}
                  onChange={(e) => setPlanForm({ ...planForm, plan_type: e.target.value })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select plan type</option>
                  <option value="per_message">Per Message</option>
                  <option value="weekly_unlimited">Weekly Unlimited</option>
                  <option value="monthly_unlimited">Monthly Unlimited</option>
                  <option value="custom">Custom Plan</option>
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Display Name*
              </label>
              <Input
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                placeholder="e.g., Per Message, Weekly Unlimited"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Description*
              </label>
              <Textarea
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Brief description of the plan..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Price (USD)*
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={planForm.price}
                onChange={(e) => setPlanForm({ ...planForm, price: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Duration (Days)
              </label>
              <Input
                type="number"
                min="0"
                value={planForm.duration_days || ''}
                onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Leave empty for per-message plans"
              />
              <p className="text-xs text-gray-500 mt-1">For subscription plans only (e.g., 7 for weekly, 30 for monthly)</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              onClick={() => {
                setShowPlanDialog(false);
                setEditingPlan(null);
                setPlanForm({ plan_type: '', name: '', description: '', price: 0, duration_days: null });
              }}
              disabled={isSubmitting}
              style={{
                borderColor: '#D66238',
                color: '#D66238'
              }}
              variant="outline"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={editingPlan ? handleUpdatePlan : handleCreatePlan}
              disabled={isSubmitting}
              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : (editingPlan ? 'Update' : 'Create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adoption Guide Section Create/Edit Dialog */}
      <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingSection ? 'Edit Adoption Guide Section' : 'Create New Section'}
            </DialogTitle>
            <DialogDescription>
              {editingSection ? 'Update section details' : 'Add a new step to the adoption guide'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Title*
              </label>
              <Input
                value={sectionForm.title}
                onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                placeholder="e.g., Facilities: Housing Requirements"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Order (Step Number)*
              </label>
              <Input
                type="number"
                min="0"
                value={sectionForm.order}
                onChange={(e) => setSectionForm({ ...sectionForm, order: parseInt(e.target.value) || 0 })}
                placeholder="1"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Custom Icon Image (Recommended)
              </label>
              <p className="text-xs text-gray-600 mb-3">Upload your own custom icon image for this section</p>
              
              <input
                type="file"
                ref={iconInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleIconImageUpload}
              />
              
              <div className="space-y-3">
                {sectionForm.icon_image_url && (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2" style={{borderColor: '#6FCFD3'}}>
                    <img 
                      src={sectionForm.icon_image_url} 
                      alt="Custom icon" 
                      className="w-full h-full object-contain bg-white p-2"
                    />
                    <button
                      type="button"
                      onClick={() => setSectionForm({ ...sectionForm, icon_image_url: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => iconInputRef.current?.click()}
                  disabled={uploadingIcon}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  {uploadingIcon ? 'Uploading...' : (sectionForm.icon_image_url ? 'Change Icon Image' : 'Upload Icon Image')}
                </Button>
                <p className="text-xs text-gray-500">Max size: 8MB. Recommended: Square images (e.g., 200x200px) with transparent background</p>
              </div>

              <div className="mt-6 pt-6 border-t">
                <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                  OR Use Pre-made Icon (Optional Fallback)
                </label>
                <p className="text-xs text-gray-600 mb-2">Only used if no custom icon is uploaded</p>
                <div className="grid grid-cols-6 gap-2 p-4 border rounded max-h-48 overflow-y-auto">
                  {availableIcons.map((icon) => {
                    const IconComponent = icon.component;
                    const isSelected = sectionForm.icon === icon.name && !sectionForm.icon_image_url;
                    return (
                      <button
                        key={icon.name}
                        type="button"
                        onClick={() => setSectionForm({ ...sectionForm, icon: icon.name })}
                        className="flex flex-col items-center justify-center p-2 rounded-lg border hover:bg-gray-50 transition"
                        style={{
                          borderColor: isSelected ? '#6FCFD3' : '#e5e7eb',
                          backgroundColor: isSelected ? '#f0fffe' : 'white',
                          opacity: sectionForm.icon_image_url ? 0.5 : 1
                        }}
                        title={icon.label}
                        disabled={!!sectionForm.icon_image_url}
                      >
                        <IconComponent 
                          className="w-5 h-5" 
                          style={{ color: isSelected ? '#6FCFD3' : '#9ca3af' }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Content* (HTML supported)
              </label>
              <Textarea
                value={sectionForm.content}
                onChange={(e) => setSectionForm({ ...sectionForm, content: e.target.value })}
                placeholder="Enter section content. You can use HTML tags like <h3>, <ul>, <li>, <strong>, <p>, etc."
                rows={15}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: Use HTML for formatting. Example: &lt;h3&gt;Title&lt;/h3&gt; &lt;ul&gt;&lt;li&gt;Point 1&lt;/li&gt;&lt;/ul&gt;
              </p>
            </div>

            {sectionForm.content && (
              <div>
                <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                  Preview
                </label>
                <div 
                  className="p-4 border rounded bg-gray-50 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sectionForm.content }}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              onClick={() => {
                setShowSectionDialog(false);
                setEditingSection(null);
                setSectionForm({ title: '', content: '', order: 0, icon: '', icon_image_url: '' });
              }}
              disabled={isSubmitting}
              style={{
                borderColor: '#D66238',
                color: '#D66238'
              }}
              variant="outline"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={editingSection ? handleUpdateSection : handleCreateSection}
              disabled={isSubmitting}
              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : (editingSection ? 'Update' : 'Create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dashboard Module Edit Dialog */}
      <Dialog open={showDashModuleDialog} onOpenChange={setShowDashModuleDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              Customize {editingDashModule?.title}
            </DialogTitle>
            <DialogDescription>
              Update the icon, colors, and description for this dashboard module
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Module Title*
              </label>
              <Input
                value={dashModuleForm.title}
                onChange={(e) => setDashModuleForm({ ...dashModuleForm, title: e.target.value })}
                placeholder="e.g., Learn & Explore"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Description*
              </label>
              <Textarea
                value={dashModuleForm.description}
                onChange={(e) => setDashModuleForm({ ...dashModuleForm, description: e.target.value })}
                placeholder="Brief description of this module..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Background Color
              </label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={dashModuleForm.bg_color}
                  onChange={(e) => setDashModuleForm({ ...dashModuleForm, bg_color: e.target.value })}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={dashModuleForm.bg_color}
                  onChange={(e) => setDashModuleForm({ ...dashModuleForm, bg_color: e.target.value })}
                  placeholder="#6FCFD3"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Choose the background color for this module's card</p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                Custom Icon Image (Recommended)
              </label>
              <p className="text-xs text-gray-600 mb-3">Upload your own custom icon for this module</p>
              
              <input
                type="file"
                ref={dashIconInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleDashIconUpload}
              />
              
              <div className="space-y-3">
                {dashModuleForm.icon_image_url && (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2" style={{borderColor: '#6FCFD3'}}>
                    <img 
                      src={dashModuleForm.icon_image_url} 
                      alt="Custom icon" 
                      className="w-full h-full object-contain bg-white p-2"
                    />
                    <button
                      type="button"
                      onClick={() => setDashModuleForm({ ...dashModuleForm, icon_image_url: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => dashIconInputRef.current?.click()}
                  disabled={uploadingDashIcon}
                  style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                >
                  {uploadingDashIcon ? 'Uploading...' : (dashModuleForm.icon_image_url ? 'Change Icon' : 'Upload Icon')}
                </Button>
                <p className="text-xs text-gray-500">Max size: 8MB. Recommended: Square, transparent background</p>
              </div>

              <div className="mt-6 pt-6 border-t">
                <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                  OR Use Pre-made Icon
                </label>
                <p className="text-xs text-gray-600 mb-2">Only used if no custom icon is uploaded</p>
                <div className="grid grid-cols-6 gap-2 p-4 border rounded max-h-48 overflow-y-auto">
                  {availableIcons.map((icon) => {
                    const IconComponent = icon.component;
                    const isSelected = dashModuleForm.icon_name === icon.name && !dashModuleForm.icon_image_url;
                    return (
                      <button
                        key={icon.name}
                        type="button"
                        onClick={() => setDashModuleForm({ ...dashModuleForm, icon_name: icon.name })}
                        className="flex flex-col items-center justify-center p-2 rounded-lg border hover:bg-gray-50 transition"
                        style={{
                          borderColor: isSelected ? '#6FCFD3' : '#e5e7eb',
                          backgroundColor: isSelected ? '#f0fffe' : 'white',
                          opacity: dashModuleForm.icon_image_url ? 0.5 : 1
                        }}
                        disabled={!!dashModuleForm.icon_image_url}
                      >
                        <IconComponent 
                          className="w-5 h-5" 
                          style={{ color: isSelected ? '#6FCFD3' : '#9ca3af' }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              onClick={() => {
                setShowDashModuleDialog(false);
                setEditingDashModule(null);
                setDashModuleForm({ title: '', description: '', icon_name: '', icon_image_url: '', bg_color: '#6FCFD3' });
              }}
              disabled={isSubmitting}
              style={{
                borderColor: '#D66238',
                color: '#D66238'
              }}
              variant="outline"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDashModule}
              disabled={isSubmitting}
              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Update Module'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Virtual Mustang Create/Edit Dialog */}
      <Dialog open={showVirtualMustangDialog} onOpenChange={setShowVirtualMustangDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingVirtualMustang ? 'Edit Virtual Mustang' : 'Add New Virtual Mustang'}
            </DialogTitle>
            <DialogDescription>
              {editingVirtualMustang ? 'Update mustang information' : 'Add a new mustang to the virtual adoption program'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={editingVirtualMustang ? handleUpdateVirtualMustang : handleCreateVirtualMustang} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Name *</label>
                <Input
                  type="text"
                  required
                  value={virtualMustangForm.name}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, name: e.target.value})}
                  placeholder="e.g., Thunder"
                />
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Age (years) *</label>
                <Input
                  type="number"
                  required
                  min="0"
                  max="30"
                  value={virtualMustangForm.age}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, age: e.target.value})}
                  placeholder="e.g., 5"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Gender *</label>
                <select
                  required
                  value={virtualMustangForm.gender}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, gender: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:outline-none focus:border-[#6FCFD3]"
                >
                  <option value="">Select...</option>
                  <option value="Stallion">Stallion</option>
                  <option value="Mare">Mare</option>
                  <option value="Gelding">Gelding</option>
                  <option value="Colt">Colt</option>
                  <option value="Filly">Filly</option>
                  <option value="Jack">Jack</option>
                  <option value="Jenny">Jenny</option>
                </select>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Color *</label>
                <Input
                  type="text"
                  required
                  value={virtualMustangForm.color}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, color: e.target.value})}
                  placeholder="e.g., Bay, Buckskin"
                />
              </div>

              {/* Height */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Height *</label>
                <Input
                  type="text"
                  required
                  value={virtualMustangForm.height}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, height: e.target.value})}
                  placeholder="e.g., 14.2 hands"
                />
              </div>

              {/* HMA */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>HMA *</label>
                <Input
                  type="text"
                  required
                  value={virtualMustangForm.hma}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, hma: e.target.value})}
                  placeholder="e.g., Sand Wash Basin"
                />
              </div>

              {/* Disposition */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Disposition *</label>
                <Input
                  type="text"
                  required
                  value={virtualMustangForm.disposition}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, disposition: e.target.value})}
                  placeholder="e.g., Spirited, Gentle, Curious"
                />
              </div>

              {/* Freezemark */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>
                  Freezemark
                </label>
                <Input
                  type="text"
                  value={virtualMustangForm.freezemark}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, freezemark: e.target.value})}
                  placeholder="e.g., 09123456"
                />
                <p className="text-xs text-gray-500 mt-1">BLM freezemark identification number</p>
              </div>

              {/* Adoption Method */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Adoption Method *</label>
                <select
                  required
                  value={virtualMustangForm.adoption_method}
                  onChange={(e) => setVirtualMustangForm({...virtualMustangForm, adoption_method: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:outline-none focus:border-[#6FCFD3]"
                >
                  <option value="olc">Online Corral (OLC)</option>
                  <option value="satellite">Satellite Events</option>
                  <option value="holding">Holding Facilities</option>
                </select>
              </div>

              {/* Photo Display - View Only */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>
                  Photos ({virtualMustangForm.photo_urls?.length || 0})
                </label>
                <div className="space-y-3">
                  {/* Existing Photos Grid */}
                  {virtualMustangForm.photo_urls && virtualMustangForm.photo_urls.length > 0 ? (
                    <div className="grid grid-cols-4 gap-3">
                      {virtualMustangForm.photo_urls.map((url, index) => (
                        <div key={index} className="relative group">
                          <div 
                            className={`w-full aspect-square rounded border-2 overflow-hidden ${index === 0 ? 'ring-2 ring-offset-2' : ''}`} 
                            style={{borderColor: index === 0 ? '#D66238' : '#6FCFD3', ringColor: '#D66238'}}
                          >
                            <img 
                              src={getImageUrl(url)}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {/* Photo Actions Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-2">
                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryMustangPhoto(index)}
                                className="p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                                title="Set as primary"
                              >
                                <Star className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveMustangPhoto(index)}
                              className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                              title="Remove photo"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          {/* Primary Badge */}
                          {index === 0 && (
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 text-xs bg-[#D66238] text-white rounded">
                              Primary
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Use the quick upload on the mustang card to add photos.</p>
                  )}
                  
                  {/* Image Position Selector */}
                  {virtualMustangForm.photo_urls && virtualMustangForm.photo_urls.length > 0 && (
                    <div className="mt-4 p-3 rounded-lg" style={{backgroundColor: '#f8f9fa', border: '1px solid #e9ecef'}}>
                      <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>
                        Image Position (how the main photo is cropped)
                      </label>
                      
                      {/* 3x3 Grid Position Selector */}
                      <div className="inline-grid grid-cols-3 gap-1 p-2 bg-gray-200 rounded-lg">
                        {[
                          { value: 'left top', label: '↖' },
                          { value: 'center top', label: '↑' },
                          { value: 'right top', label: '↗' },
                          { value: 'left center', label: '←' },
                          { value: 'center center', label: '●' },
                          { value: 'right center', label: '→' },
                          { value: 'left bottom', label: '↙' },
                          { value: 'center bottom', label: '↓' },
                          { value: 'right bottom', label: '↘' }
                        ].map((pos) => (
                          <button
                            key={pos.value}
                            type="button"
                            onClick={() => setVirtualMustangForm({...virtualMustangForm, image_position: pos.value})}
                            className={`w-10 h-10 rounded flex items-center justify-center text-lg font-bold transition-all ${
                              (virtualMustangForm.image_position || 'center center') === pos.value 
                                ? 'bg-[#D66238] text-white shadow-md' 
                                : 'bg-white hover:bg-[#6FCFD3] hover:text-white'
                            }`}
                            title={pos.value}
                          >
                            {pos.label}
                          </button>
                        ))}
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Tap to choose which part of the image to show when cropped. Current: <strong>{virtualMustangForm.image_position || 'center center'}</strong>
                      </p>
                      
                      {/* Live Preview */}
                      {virtualMustangForm.photo_urls[0] && (
                        <div className="mt-3 flex items-start gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-600 mb-1">Card Preview:</p>
                            <div className="w-24 h-32 rounded-lg overflow-hidden border-2" style={{borderColor: '#6FCFD3'}}>
                              <img 
                                src={getImageUrl(virtualMustangForm.photo_urls[0])}
                                alt="Position preview"
                                className="w-full h-full object-cover"
                                style={{objectPosition: virtualMustangForm.image_position || 'center center'}}
                              />
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-600 mb-1">Original:</p>
                            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-300">
                              <img 
                                src={getImageUrl(virtualMustangForm.photo_urls[0])}
                                alt="Original"
                                className="w-full h-full object-contain bg-gray-100"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1"
                style={{backgroundColor: '#D66238', color: 'white'}}
              >
                {editingVirtualMustang ? 'Update Mustang' : 'Create Mustang'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowVirtualMustangDialog(false);
                  setEditingVirtualMustang(null);
                  setVirtualMustangForm({
                    name: '',
                    age: 0,
                    gender: '',
                    color: '',
                    hma: '',
                    height: '',
                    disposition: '',
                    photo_url: '',
                    photo_urls: [],
                    adoption_method: 'olc'
                  });
                }}
                style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>Create New Admin User</DialogTitle>
            <DialogDescription>
              Create a new user account with admin privileges. They will be able to login immediately with the provided credentials.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Full Name *</label>
              <Input
                type="text"
                required
                value={createUserForm.name}
                onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                placeholder="John Doe"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Email *</label>
              <Input
                type="email"
                required
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                placeholder="admin@example.com"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: '#D66238'}}>Password *</label>
              <Input
                type="password"
                required
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                placeholder="Create a strong password"
                className="w-full"
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>
            
            <div className="flex items-center gap-2 p-3 rounded-lg" style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}>
              <input
                type="checkbox"
                checked={createUserForm.is_admin}
                onChange={(e) => setCreateUserForm({...createUserForm, is_admin: e.target.checked})}
                className="w-4 h-4"
                style={{accentColor: '#6FCFD3'}}
              />
              <label className="text-sm font-medium" style={{color: '#D66238'}}>
                <Shield className="w-4 h-4 inline mr-1" />
                Grant Admin Access
              </label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1"
                style={{backgroundColor: '#D66238', color: 'white'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCreateUserDialog(false);
                  setCreateUserForm({
                    name: '',
                    email: '',
                    password: '',
                    is_admin: true
                  });
                }}
                style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ranch Block Create/Edit Dialog */}
      <Dialog open={showRanchBlockDialog} onOpenChange={setShowRanchBlockDialog}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingRanchBlock ? 'Edit Ranch Block' : 'Create Ranch Block'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingRanchBlock ? handleUpdateRanchBlock : handleCreateRanchBlock} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Block Title</label>
              <Input
                value={ranchBlockForm.title}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, title: e.target.value})}
                placeholder="e.g., Welcome Banner, Custom Widget"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Position</label>
              <select
                value={ranchBlockForm.position}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, position: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="main">Main Content</option>
                <option value="top">Top of Page</option>
                <option value="bottom">Bottom of Page</option>
                <option value="sidebar">Sidebar</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Order (lower numbers appear first)</label>
              <Input
                type="number"
                value={ranchBlockForm.order}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, order: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">HTML Content</label>
              <Textarea
                value={ranchBlockForm.html_content}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, html_content: e.target.value})}
                placeholder="<div>Your HTML content here</div>"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Write any HTML markup you want to display</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">CSS Content (optional)</label>
              <Textarea
                value={ranchBlockForm.css_content}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, css_content: e.target.value})}
                placeholder=".my-game { background: #000; color: #fff; }"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">CSS styles for your mini game (without &lt;style&gt; tags)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Assets URLs (optional)</label>
              <Textarea
                value={Array.isArray(ranchBlockForm.assets) ? ranchBlockForm.assets.join('\n') : ''}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, assets: e.target.value.split('\n').filter(url => url.trim())})}
                placeholder="https://example.com/image1.png&#10;https://example.com/sound.mp3&#10;https://example.com/sprite.png"
                rows={4}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">One asset URL per line (images, sounds, sprites for your game)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">JavaScript Content (optional)</label>
              <Textarea
                value={ranchBlockForm.js_content}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, js_content: e.target.value})}
                placeholder="console.log('Hello from My Ranch!');"
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">JavaScript will execute after the HTML is rendered</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={ranchBlockForm.is_active}
                onChange={(e) => setRanchBlockForm({...ranchBlockForm, is_active: e.target.checked})}
                id="is_active"
              />
              <label htmlFor="is_active" className="text-sm">Active (show on My Ranch page)</label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowRanchBlockDialog(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" style={{backgroundColor: '#6FCFD3', color: '#fff'}} className="w-full sm:w-auto">
                {editingRanchBlock ? 'Update Block' : 'Create Block'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Daily Care Category Dialog */}
      <Dialog open={showDailyCareDialog} onOpenChange={setShowDailyCareDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingDailyCareCategory !== null ? 'Edit Daily Care Category' : 'Add Daily Care Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category Key</label>
                <Input
                  value={dailyCareForm.key}
                  onChange={(e) => setDailyCareForm({...dailyCareForm, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., grain"
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, no spaces)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category Label</label>
                <Input
                  value={dailyCareForm.label}
                  onChange={(e) => setDailyCareForm({...dailyCareForm, label: e.target.value})}
                  placeholder="e.g., Grain"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Category Icon (optional)</label>
              <div className="flex items-center gap-3">
                {dailyCareForm.icon_url && (
                  <img src={getImageUrl(dailyCareForm.icon_url)} alt="Icon" className="w-10 h-10 object-contain border rounded" />
                )}
                <input
                  type="file"
                  ref={herdCategoryIconInputRef}
                  onChange={(e) => handleHerdCategoryIconUpload(e, 'daily_care')}
                  accept={ACCEPTED_IMAGE_TYPES}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => herdCategoryIconInputRef.current?.click()}
                  disabled={uploadingHerdIcon}
                >
                  {uploadingHerdIcon ? 'Uploading...' : 'Upload Icon'}
                </Button>
                {dailyCareForm.icon_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDailyCareForm({...dailyCareForm, icon_url: ''})}
                    className="text-red-500"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Items (Checkboxes)</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {dailyCareForm.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={item.key}
                      onChange={(e) => {
                        const newItems = [...dailyCareForm.items];
                        newItems[index] = {...item, key: e.target.value.toLowerCase().replace(/\s+/g, '_')};
                        setDailyCareForm({...dailyCareForm, items: newItems});
                      }}
                      placeholder="Key (e.g., am)"
                      className="flex-1"
                    />
                    <Input
                      value={item.label}
                      onChange={(e) => {
                        const newItems = [...dailyCareForm.items];
                        newItems[index] = {...item, label: e.target.value};
                        setDailyCareForm({...dailyCareForm, items: newItems});
                      }}
                      placeholder="Label (e.g., AM)"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newItems = dailyCareForm.items.filter((_, i) => i !== index);
                        setDailyCareForm({...dailyCareForm, items: newItems.length > 0 ? newItems : [{ key: '', label: '' }]});
                      }}
                      className="text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDailyCareForm({...dailyCareForm, items: [...dailyCareForm.items, { key: '', label: '' }]})}
                className="mt-2"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={handleSaveDailyCareCategory} style={{backgroundColor: '#D66238', color: 'white'}} className="flex-1">
                Save Category
              </Button>
              <Button variant="outline" onClick={() => setShowDailyCareDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Training Category Dialog */}
      <Dialog open={showTrainingDialog} onOpenChange={setShowTrainingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{color: '#D66238'}}>
              {editingTrainingCategory !== null ? 'Edit Training Category' : 'Add Training Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category Key</label>
                <Input
                  value={trainingForm.key}
                  onChange={(e) => setTrainingForm({...trainingForm, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., halter"
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, no spaces)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category Label</label>
                <Input
                  value={trainingForm.label}
                  onChange={(e) => setTrainingForm({...trainingForm, label: e.target.value})}
                  placeholder="e.g., Halter Training"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Items (Checkboxes)</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {trainingForm.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={item.key}
                      onChange={(e) => {
                        const newItems = [...trainingForm.items];
                        newItems[index] = {...item, key: e.target.value.toLowerCase().replace(/\s+/g, '_')};
                        setTrainingForm({...trainingForm, items: newItems});
                      }}
                      placeholder="Key (e.g., introduce_halter)"
                      className="flex-1"
                    />
                    <Input
                      value={item.label}
                      onChange={(e) => {
                        const newItems = [...trainingForm.items];
                        newItems[index] = {...item, label: e.target.value};
                        setTrainingForm({...trainingForm, items: newItems});
                      }}
                      placeholder="Label (e.g., Introduce halter)"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newItems = trainingForm.items.filter((_, i) => i !== index);
                        setTrainingForm({...trainingForm, items: newItems.length > 0 ? newItems : [{ key: '', label: '' }]});
                      }}
                      className="text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTrainingForm({...trainingForm, items: [...trainingForm.items, { key: '', label: '' }]})}
                className="mt-2"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={handleSaveTrainingCategory} style={{backgroundColor: '#D66238', color: 'white'}} className="flex-1">
                Save Category
              </Button>
              <Button variant="outline" onClick={() => setShowTrainingDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminPanel;
